import * as THREE from 'three';
import { ArcballControls } from 'three/addons/controls/ArcballControls.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import { Sky } from 'three/addons/objects/Sky.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { OutlinePass } from 'three/addons/postprocessing/OutlinePass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import LabeledGrid from './LabeledGrid';
import { DimensionOverlay, type DimensionSetup } from './DimensionOverlay';
import { MeasureTool, type MeasurePick, type MeasureMode } from './MeasureTool';
import { STLExporter } from 'three/addons/exporters/STLExporter.js';
import { zipSync, strToU8 } from 'fflate';
import type { SourcePosition } from '../scad/errors';
import type { ViewportTheme } from '../app/theme';

// Snapshot of the 3D camera for the sketch "3D BG" mode so we can restore
// the user's exact view on exit — and remember their last BG view across
// toggles. Kept alongside ortho frustum so projection + zoom restore cleanly.
export interface ViewSnapshot {
  isOrthographic: boolean;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  up: THREE.Vector3;
  target: THREE.Vector3;
  ortho?: { left: number; right: number; top: number; bottom: number };
}

export interface GhostPart {
  mesh: THREE.Mesh;        // invisible mesh for raycasting
  from: SourcePosition;
  to: SourcePosition;
  center: THREE.Vector3;   // world-space center
  /** Module-call instance (shape inside a module used more than once). */
  instance?: string;
}

export interface MeshPart {
  mesh: THREE.Mesh;
  edges: THREE.LineSegments;
  from: SourcePosition;
  to: SourcePosition;
  initialPosition: THREE.Vector3;
  children?: GhostPart[];           // ghost meshes for boolean group children
  operation?: 'union' | 'difference' | 'intersection';
  color?: [number, number, number]; // RGB 0-1
  opacity?: number;                  // 0-1
  /** Colored pieces drawn instead of `mesh` (see GeometryPart.display). */
  displayMeshes?: THREE.Mesh[];
}

export interface TransformEndEvent {
  mode: 'translate' | 'rotate' | 'scale';
  position: THREE.Vector3;
  rotation: THREE.Euler;
  scale: THREE.Vector3;
  from: SourcePosition;
  to: SourcePosition;
}

export class ViewportManager {
  private container: HTMLElement;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  private controls: ArcballControls | OrbitControls;
  private perspCamera: THREE.PerspectiveCamera;   // kept alive across ortho toggles
  private arcControls: ArcballControls;            // kept alive across ortho toggles
  private currentMesh: THREE.Mesh | null = null;
  private edgeLines: THREE.LineSegments | null = null;
  private debugMesh: THREE.Mesh | null = null;
  /** Active section (clipping) plane in Three world space, or null. */
  private sectionPlane: THREE.Plane | null = null;
  private sectionCapMesh: THREE.Mesh | null = null;
  /** One-shot pick (snap target): consumes the next model click. */
  private pickOnceHandler: ((e: PointerEvent) => void) | null = null;
  private snapPreview: THREE.Box3Helper | null = null;
  private material: THREE.MeshPhongMaterial;
  private wireframeMaterial: THREE.MeshPhongMaterial;
  private debugMaterial: THREE.MeshPhongMaterial;
  private composer: EffectComposer;
  private outlinePass: OutlinePass;
  private selectionOutlinePass: OutlinePass;
  private grid: LabeledGrid;
  private isWireframe = false;
  private animationId = 0;
  private resizeObserver: ResizeObserver;
  private gridExtent = 50; // half of 100mm default grid
  private cameraFitted = false;
  private isOrthographic = false;
  private perspectiveFov = 50;

  // Frame wireframe boxes (dashed lines showing virtual bounding boxes)
  private frameLines: THREE.LineSegments[] = [];
  private frameMaterial: THREE.LineDashedMaterial;

  // Multi-mesh / selection state
  private meshParts: MeshPart[] = [];
  private selectedPart: MeshPart | null = null;
  private selectedGhost: GhostPart | null = null;
  private ghostHighlight: THREE.Mesh | null = null;
  private ghostHighlightMaterial: THREE.MeshPhongMaterial;
  private raycaster = new THREE.Raycaster();
  private transformControls: TransformControls;
  private transformEndCallback: ((event: TransformEndEvent) => void) | null = null;
  private modeChangeCallback: ((mode: string, selected: boolean) => void) | null = null;
  private childSelectedCallback: ((from: SourcePosition, to: SourcePosition) => void) | null = null;
  private selectionClearedCallback: (() => void) | null = null;
  private transformLiveCallback: (() => void) | null = null;

  // Dimension overlay
  private dimensionOverlay: DimensionOverlay;
  private measureTool: MeasureTool;
  private dimensionEditCallback: ((from: number, to: number, value: string) => void) | null = null;

  // Combined axis+viewcube HUD (top-right corner)
  private hudScene: THREE.Scene;
  private hudCamera: THREE.PerspectiveCamera;
  private viewCubeMesh: THREE.Mesh;
  private viewCubeProxy: THREE.Mesh;
  private hudRaycaster = new THREE.Raycaster();
  private viewCubeEdges: THREE.LineSegments;
  private sky: Sky;

  // Theme-dependent viewport colors
  private themeGridColor = 0x222d3a;
  private themeGridTextColor = '#3a4a5e';
  private themeGridCmBorderColor = 'rgba(58,74,94,';
  private themeGridLabelStrokeColor = 'rgba(255,255,255,0.7)';
  private themeOutlineVisibleColor = 0x000000;
  private themeOutlineHiddenColor = 0x222222;

  // Snap settings (shared with DimensionOverlay)
  private snapEnabled = true;
  private snapStep = 0.1;
  private rotationSnapDeg = 5;
  private shiftHeld = false;
  private rotationIndicators: Map<string, THREE.Line> = new Map();
  private rotationHandlePositions: Map<string, THREE.Vector3> = new Map();
  private rotationDetailGroups: Map<string, THREE.Group> = new Map();
  private rotationHandleGroups: Map<string, THREE.Group> = new Map();
  private rotationOverlay: THREE.Group | null = null; // our custom rotation elements (separate from TC's gizmo children)
  private hoveredRotationAxis: string | null = null;
  private draggingRotation = false;
  private lastMouseNDC = new THREE.Vector2();
  private rotationDragStartDir: THREE.Vector3 | null = null; // unit direction on circle at drag start

  // View-only mode (preview): disable selection and transform
  private isViewOnly = false;

  // Click-vs-drag detection
  private pointerDownPos = { x: 0, y: 0 };
  private pointerDownTime = 0;
  private gizmoDragOccurred = false;

  // User-defined origin (work coordinate system) — scene-space (Three.js Y-up)
  private userOrigin = new THREE.Vector3(0, 0, 0);
  private originGroup: THREE.Group | null = null;
  private originChangeCallbacks: Array<(origin: THREE.Vector3) => void> = [];
  private static ORIGIN_STORAGE_KEY = 'scadplus-user-origin';

  constructor(container: HTMLElement) {
    this.container = container;

    // Renderer — transparent clear so the .vp-stage CSS grid shows through
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setClearColor(0x000000, 0);
    container.appendChild(this.renderer.domElement);

    // Scene
    this.scene = new THREE.Scene();

    // Sky (kept in-scene for env lighting references but hidden so the viewport
    // shows only the clear color + grid)
    this.sky = new Sky();
    this.sky.scale.setScalar(1000);
    this.sky.visible = false;
    this.scene.add(this.sky);

    const skyUniforms = this.sky.material.uniforms;
    skyUniforms['turbidity'].value = 0.1;
    skyUniforms['rayleigh'].value = 0.08;
    skyUniforms['mieCoefficient'].value = 0.0001;
    skyUniforms['mieDirectionalG'].value = 0.05;

    const sun = new THREE.Vector3();
    const phi = THREE.MathUtils.degToRad(60);
    const theta = THREE.MathUtils.degToRad(180);
    sun.setFromSphericalCoords(1, phi, theta);
    skyUniforms['sunPosition'].value.copy(sun);

    // Camera
    this.perspCamera = new THREE.PerspectiveCamera(50, 1, 0.01, 2000);
    this.perspCamera.position.set(15, 12, 15);
    this.perspCamera.lookAt(0, 0, 0);
    this.camera = this.perspCamera;

    // Controls (ArcballControls kept alive across ortho toggles)
    this.arcControls = new ArcballControls(this.perspCamera, this.renderer.domElement, this.scene);
    this.arcControls.cursorZoom = true;
    this.arcControls.setGizmosVisible(false);
    this.controls = this.arcControls;

    // TransformControls
    this.transformControls = new TransformControls(this.camera, this.renderer.domElement);
    this.transformControls.setMode('translate');
    this.transformControls.setSize(0.8);
    this.scene.add(this.transformControls.getHelper());
    this.cleanupRotationGizmo();
    this.recolorGizmoForSCAD();

    // Track Shift key for coarse rotation snap (45° when held)
    window.addEventListener('keydown', (e) => { if (e.key === 'Shift') this.shiftHeld = true; });
    window.addEventListener('keyup', (e) => { if (e.key === 'Shift') this.shiftHeld = false; });

    // Track mouse position for rotation indicator + handle hover detection
    this.container.addEventListener('pointermove', (e) => {
      const rect = this.container.getBoundingClientRect();
      this.lastMouseNDC.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
      this.updateRotationHandleHover(e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height);
    });

    // Block rotation from non-handle clicks + force correct axis — capture phase runs before TC's handler
    this.renderer.domElement.addEventListener('pointerdown', (e) => {
      if (this.transformControls.mode !== 'rotate') return;
      if (!this.rotationOverlay || !this.rotationOverlay.visible) return;
      const rect = this.container.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      this.rotationOverlay.updateMatrixWorld(true);
      let nearAxis: string | null = null;
      let nearDist = Infinity;
      for (const [name, localPos] of this.rotationHandlePositions) {
        const worldPos = localPos.clone().applyMatrix4(this.rotationOverlay.matrixWorld);
        const ndc = worldPos.clone().project(this.camera);
        const px = (ndc.x + 1) / 2 * rect.width;
        const py = (-ndc.y + 1) / 2 * rect.height;
        const dist = Math.sqrt((mx - px) ** 2 + (my - py) ** 2);
        if (dist < 30 && dist < nearDist) {
          nearAxis = name;
          nearDist = dist;
        }
      }
      if (!nearAxis) {
        // Temporarily disable TC so it won't start a rotation drag
        this.transformControls.enabled = false;
        requestAnimationFrame(() => { this.transformControls.enabled = true; });
      } else {
        // Temporarily hide other axis pickers so TC only detects the correct axis
        const internalGizmo = (this.transformControls as any)._gizmo;
        const rotatePicker = internalGizmo?.picker?.['rotate'];
        if (rotatePicker) {
          const hidden: THREE.Object3D[] = [];
          for (const child of rotatePicker.children) {
            if (child.name !== nearAxis && child.name !== 'XYZE') {
              child.visible = false;
              hidden.push(child);
            }
          }
          // Restore after TC processes the event (next frame)
          requestAnimationFrame(() => {
            for (const child of hidden) child.visible = true;
          });
        }
      }
    }, true);

    // Disable orbit while dragging gizmo, track drag state
    this.transformControls.addEventListener('dragging-changed', (event: any) => {
      this.controls.enabled = !event.value;
      if (event.value) {
        this.gizmoDragOccurred = true;
        // Drag started — activate rotation axis (hide others, show detail)
        if (this.transformControls.mode === 'rotate') {
          this.draggingRotation = true;
          const activeAxis = (this.transformControls as any).axis as string | null;
          if (activeAxis) {
            this.activateRotationAxis(activeAxis);
            // Use the handle's position as the indicator start direction (so needle aligns with handle)
            const handlePos = this.rotationHandlePositions.get(activeAxis);
            this.rotationDragStartDir = handlePos ? handlePos.clone().normalize() : null;
          }
        }
      } else {
        // Drag ended — restore all axes, hide indicators
        if (this.draggingRotation) {
          this.draggingRotation = false;
          this.rotationDragStartDir = null;
          this.deactivateRotationAxes();
        }
        for (const ind of this.rotationIndicators.values()) ind.visible = false;
      }
    });

    // Sync edges and ghost highlight during gizmo drag
    this.transformControls.addEventListener('change', () => {
      if (this.selectedPart) {
        const target = (this.selectedGhost && this.ghostHighlight)
          ? this.ghostHighlight : this.selectedPart.mesh;

        const mode = this.transformControls.mode;
        if (this.snapEnabled) {
          if (mode === 'translate') {
            target.position.x = this.snapValue(target.position.x);
            target.position.y = this.snapValue(target.position.y);
            target.position.z = this.snapValue(target.position.z);
          } else if (mode === 'rotate') {
            const deg = this.shiftHeld ? 45 : this.rotationSnapDeg;
            const snap = THREE.MathUtils.degToRad(deg);
            target.rotation.x = Math.round(target.rotation.x / snap) * snap;
            target.rotation.y = Math.round(target.rotation.y / snap) * snap;
            target.rotation.z = Math.round(target.rotation.z / snap) * snap;
          }
        }
        if (mode === 'rotate' && this.draggingRotation) {
          this.updateRotationIndicator(target.rotation);
        }

        if (!(this.selectedGhost && this.ghostHighlight)) {
          // Sync edges to mesh
          this.selectedPart.edges.position.copy(this.selectedPart.mesh.position);
          this.selectedPart.edges.quaternion.copy(this.selectedPart.mesh.quaternion);
          this.selectedPart.edges.scale.copy(this.selectedPart.mesh.scale);
        }
        if (this.transformLiveCallback) this.transformLiveCallback();
      }
    });

    // Fire transform-end callback on mouseUp after gizmo drag
    this.transformControls.addEventListener('mouseUp', () => {
      if (!this.gizmoDragOccurred) return;

      if (this.transformEndCallback && this.selectedPart) {
        let deltaPos: THREE.Vector3;
        let rot: THREE.Euler;
        let scl: THREE.Vector3;
        let from: SourcePosition;
        let to: SourcePosition;

        if (this.selectedGhost && this.ghostHighlight) {
          // Ghost child drag: compute delta from ghostHighlight vs original ghost center
          deltaPos = this.ghostHighlight.position.clone().sub(this.selectedGhost.center);
          rot = this.ghostHighlight.rotation.clone();
          scl = this.ghostHighlight.scale.clone();
          from = this.selectedGhost.from;
          to = this.selectedGhost.to;
        } else {
          // Top-level part drag
          deltaPos = this.selectedPart.mesh.position.clone().sub(this.selectedPart.initialPosition);
          rot = this.selectedPart.mesh.rotation.clone();
          scl = this.selectedPart.mesh.scale.clone();
          from = this.selectedPart.from;
          to = this.selectedPart.to;
        }

        // Check if the transform is meaningful (not a micro-click)
        const hasMoved = deltaPos.lengthSq() > 0.0001;
        const hasRotated = Math.abs(rot.x) > 0.001 || Math.abs(rot.y) > 0.001 || Math.abs(rot.z) > 0.001;
        const hasScaled = Math.abs(scl.x - 1) > 0.001 || Math.abs(scl.y - 1) > 0.001 || Math.abs(scl.z - 1) > 0.001;

        if (hasMoved || hasRotated || hasScaled) {
          this.transformEndCallback({
            mode: this.transformControls.mode as 'translate' | 'rotate' | 'scale',
            position: deltaPos,
            rotation: rot,
            scale: scl,
            from,
            to,
          });
          // Keep gizmoDragOccurred=true so onPointerUp skips click handling
          return;
        }
      }

      // Transform was negligible — clear flag so onPointerUp can process as a click
      this.gizmoDragOccurred = false;
    });

    // Lighting — 3-point setup with hemisphere for natural sky feel
    const hemiLight = new THREE.HemisphereLight(0x87ceeb, 0x444444, 0.8);
    this.scene.add(hemiLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 1.5);
    keyLight.position.set(5, 10, 7);
    this.scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xaaccff, 0.6);
    fillLight.position.set(-5, 4, -3);
    this.scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 0.4);
    rimLight.position.set(0, 2, -10);
    this.scene.add(rimLight);

    // Finite labeled grid (replaces ThreeInfiniteGrid)
    this.grid = this.createGrid();
    this.scene.add(this.grid);

    // User-origin gizmo (hidden until origin ≠ world)
    this.originGroup = this.buildOriginGizmo();
    this.scene.add(this.originGroup);
    this.loadUserOriginFromStorage();

    // Materials
    this.material = new THREE.MeshPhongMaterial({
      color: 0xb5ffcd,
      specular: 0x666666,
      shininess: 60,
      flatShading: false,
      side: THREE.DoubleSide,
    });

    this.wireframeMaterial = new THREE.MeshPhongMaterial({
      color: 0xb5ffcd,
      specular: 0x666666,
      shininess: 60,
      flatShading: false,
      side: THREE.DoubleSide,
      wireframe: true,
    });

    // Debug material: translucent pink-red for # modifier
    this.debugMaterial = new THREE.MeshPhongMaterial({
      color: 0xff3366,
      specular: 0x222222,
      shininess: 20,
      flatShading: false,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
    });

    // Ghost highlight material: translucent orange for selected boolean child
    this.ghostHighlightMaterial = new THREE.MeshPhongMaterial({
      color: 0xffaa00, specular: 0x222222, shininess: 20,
      flatShading: false, side: THREE.DoubleSide,
      transparent: true, opacity: 0.35,
      depthWrite: false, depthTest: false,
    });

    // Frame wireframe material: dashed lines for virtual bounding boxes
    this.frameMaterial = new THREE.LineDashedMaterial({
      color: 0xffdd44,
      dashSize: 1,
      gapSize: 0.5,
    });

    // Post-processing: outline pass for silhouette edges
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    this.outlinePass = new OutlinePass(
      new THREE.Vector2(container.clientWidth || 1, container.clientHeight || 1),
      this.scene,
      this.camera,
    );
    this.outlinePass.edgeStrength = 3;
    this.outlinePass.edgeGlow = 0;
    this.outlinePass.edgeThickness = 1;
    this.outlinePass.visibleEdgeColor.set(0x000000);
    this.outlinePass.hiddenEdgeColor.set(0x222222);
    this.composer.addPass(this.outlinePass);

    // Selection highlight outline pass (orange)
    this.selectionOutlinePass = new OutlinePass(
      new THREE.Vector2(container.clientWidth || 1, container.clientHeight || 1),
      this.scene,
      this.camera,
    );
    this.selectionOutlinePass.edgeStrength = 5;
    this.selectionOutlinePass.edgeGlow = 0.5;
    this.selectionOutlinePass.edgeThickness = 2;
    this.selectionOutlinePass.visibleEdgeColor.set(0xffaa00);
    this.selectionOutlinePass.hiddenEdgeColor.set(0xffaa00);
    this.selectionOutlinePass.selectedObjects = [];
    this.composer.addPass(this.selectionOutlinePass);

    this.composer.addPass(new OutputPass());

    // Click handling for selection
    this.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
    this.renderer.domElement.addEventListener('pointerup', this.onPointerUp);
    this.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
    this.renderer.domElement.addEventListener('contextmenu', this.onContextMenu);

    // Resize observer
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(container);

    // Dimension overlay (3D lines + HTML labels + drag handles for dimension annotations)
    this.dimensionOverlay = new DimensionOverlay(container, this.camera, this.scene,
      (from, to, value) => { if (this.dimensionEditCallback) this.dimensionEditCallback(from, to, value); },
      (dragging) => { this.controls.enabled = !dragging; },
    );

    // Measure tool (distance / angle / radius) — consumes clicks while active
    this.measureTool = new MeasureTool(container, this.scene, () => this.camera,
      (x, y) => this.pickSnapPoint(x, y));

    // Combined axis+viewcube HUD (rendered in top-right corner)
    this.hudScene = new THREE.Scene();
    this.hudCamera = new THREE.PerspectiveCamera(50, 1, 0.1, 10);

    // View cube: face order +X, -X, +Y, -Y, +Z, -Z → Right, Left, Top, Bottom, Front, Back
    const cubeFaceLabels = ['Right', 'Left', 'Top', 'Bottom', 'Front', 'Back'];
    const cubeFaceColors = [0x4a6a8a, 0x4a6a8a, 0x5a7a9a, 0x3a5a7a, 0x4a6a8a, 0x4a6a8a];
    const cubeFaceMats = cubeFaceLabels.map((label, idx) => {
      const s = 256;
      const canvas = document.createElement('canvas');
      canvas.width = s;
      canvas.height = s;
      const ctx = canvas.getContext('2d')!;
      const c = new THREE.Color(cubeFaceColors[idx]);
      ctx.fillStyle = `rgb(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)})`;
      ctx.fillRect(0, 0, s, s);
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth = 4;
      ctx.strokeRect(2, 2, s - 4, s - 4);
      ctx.font = 'bold 52px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(label, s / 2, s / 2);
      const texture = new THREE.CanvasTexture(canvas);
      return new THREE.MeshBasicMaterial({ map: texture });
    });

    const cubeSize = 0.7;
    const cubeGeo = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);
    this.viewCubeMesh = new THREE.Mesh(cubeGeo, cubeFaceMats);
    this.viewCubeMesh.name = 'viewCube';
    this.hudScene.add(this.viewCubeMesh);

    // Cube edge outlines for visual clarity
    const cubeEdgesGeo = new THREE.EdgesGeometry(cubeGeo);
    this.viewCubeEdges = new THREE.LineSegments(cubeEdgesGeo, new THREE.LineBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0.4,
    }));
    this.hudScene.add(this.viewCubeEdges);

    // Invisible proxy cube (larger) to catch clicks that miss the inner cube (edges/corners)
    const proxyGeo = new THREE.BoxGeometry(1.4, 1.4, 1.4);
    this.viewCubeProxy = new THREE.Mesh(proxyGeo, new THREE.MeshBasicMaterial({ visible: false }));
    this.viewCubeProxy.name = 'viewCubeProxy';
    this.hudScene.add(this.viewCubeProxy);

    // Axis arrows (defined in SCAD Z-up space, rotated group converts to Three.js Y-up)
    // Each arrow starts at a cube edge — the corner where its positive face meets the two negative faces
    const cubeHalf = cubeSize / 2;
    const arrowGap = 0.08;
    const arrowStart = cubeHalf + arrowGap;
    const arrowLen = 0.5;
    const headLen = 0.15;
    const headW = 0.10;
    // All three arrows share one corner, each running along a cube edge then extending past
    const corner = new THREE.Vector3(-cubeHalf, -cubeHalf, -cubeHalf);
    const totalLen = cubeSize + 0.3;
    const labelOff = totalLen + 0.12;
    const axisGroup = new THREE.Group();
    axisGroup.rotateX(-Math.PI / 2);
    axisGroup.add(new THREE.ArrowHelper(new THREE.Vector3(1, 0, 0), corner, totalLen, 0xff4444, headLen, headW));
    axisGroup.add(new THREE.ArrowHelper(new THREE.Vector3(0, 1, 0), corner, totalLen, 0x44cc44, headLen, headW));
    axisGroup.add(new THREE.ArrowHelper(new THREE.Vector3(0, 0, 1), corner, totalLen, 0x4488ff, headLen, headW));
    axisGroup.add(this.makeAxisLabel('X', 0xff4444, new THREE.Vector3(-cubeHalf + labelOff, -cubeHalf, -cubeHalf)));
    axisGroup.add(this.makeAxisLabel('Y', 0x44cc44, new THREE.Vector3(-cubeHalf, -cubeHalf + labelOff, -cubeHalf)));
    axisGroup.add(this.makeAxisLabel('Z', 0x4488ff, new THREE.Vector3(-cubeHalf, -cubeHalf, -cubeHalf + labelOff)));
    this.hudScene.add(axisGroup);

    // Soft ambient light for the HUD
    this.hudScene.add(new THREE.AmbientLight(0xffffff, 1));

    // Initial size
    this.handleResize();

    // Start render loop
    this.animate();
  }

  private onPointerDown = (e: PointerEvent): void => {
    this.pointerDownPos = { x: e.clientX, y: e.clientY };
    this.pointerDownTime = performance.now();
  };

  getMeasureTool(): MeasureTool {
    return this.measureTool;
  }

  /**
   * Snapshot of the 3D view as a PNG data URL, composited onto the page
   * background (the WebGL canvas is transparent). Renders a fresh frame in
   * the same tick, so no preserveDrawingBuffer is needed.
   */
  captureImage(): string {
    this.composer.render();
    const src = this.renderer.domElement;
    const out = document.createElement('canvas');
    out.width = src.width;
    out.height = src.height;
    const ctx = out.getContext('2d')!;
    const css = getComputedStyle(document.documentElement);
    ctx.fillStyle = css.getPropertyValue('--bg').trim() || '#16161a';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(src, 0, 0);
    return out.toDataURL('image/png');
  }

  /** Enter/leave a measure mode (null = off). Clears selection while measuring. */
  setMeasureMode(mode: MeasureMode | null): void {
    if (mode) this.deselectAll();
    this.measureTool.setMode(mode);
    this.renderer.domElement.style.cursor = mode ? 'crosshair' : '';
  }

  /** Three (Y-up) box → SCAD (Z-up) bounds. */
  private static toScadBounds(b: THREE.Box3): { min: [number, number, number]; max: [number, number, number] } {
    return { min: [b.min.x, -b.max.z, b.min.y], max: [b.max.x, -b.min.z, b.max.y] };
  }

  private static meshWorldBox(mesh: THREE.Mesh): THREE.Box3 | null {
    mesh.geometry.computeBoundingBox();
    const bb = mesh.geometry.boundingBox;
    return bb ? bb.clone().translate(mesh.position) : null;
  }

  /** World AABB of the selection in SCAD coordinates, or null. */
  getSelectedScadBounds(): { min: [number, number, number]; max: [number, number, number] } | null {
    const info = this.getSelectedBBox();
    return info ? ViewportManager.toScadBounds(info.bbox) : null;
  }

  /**
   * For a selected child: bounds of the rest of its group — the other
   * children of the same part that neither contain nor are contained in the
   * selection (e.g. the cube next to a selected cylinder in a union) — or the
   * whole part when it has no such siblings. SCAD coordinates.
   */
  getParentScadBounds(): { min: [number, number, number]; max: [number, number, number] } | null {
    const sel = this.selectedGhost;
    if (!sel || !this.selectedPart) return null;
    const box = new THREE.Box3();
    const overlaps = (g: GhostPart) => g.from.offset < sel.to.offset && sel.from.offset < g.to.offset;
    for (const g of this.selectedPart.children ?? []) {
      if (g === sel || overlaps(g)) continue;
      const b = ViewportManager.meshWorldBox(g.mesh);
      if (b) box.union(b);
    }
    if (box.isEmpty()) {
      const whole = ViewportManager.meshWorldBox(this.selectedPart.mesh);
      if (whole) box.copy(whole);
    }
    return box.isEmpty() ? null : ViewportManager.toScadBounds(box);
  }

  /**
   * Consume the next click on the model: 'point' resolves a snapped point
   * (SCAD), 'part' the SCAD bounds of the clicked part/child. A click on
   * empty space resolves null. Escape → cancelPickOnce().
   */
  pickOnce(kind: 'point' | 'part', cb: (result: { point?: [number, number, number]; bounds?: { min: [number, number, number]; max: [number, number, number] } } | null) => void): void {
    this.cancelPickOnce();
    this.renderer.domElement.style.cursor = 'crosshair';
    if (kind === 'point') this.measureTool.setHoverOnly(true);
    this.pickOnceHandler = (e) => {
      this.cancelPickOnce();
      if (kind === 'point') {
        const p = this.pickSnapPoint(e.clientX, e.clientY);
        cb(p ? { point: [p.point.x, -p.point.z, p.point.y] } : null);
        return;
      }
      const rect = this.renderer.domElement.getBoundingClientRect();
      this.raycaster.setFromCamera(new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      ), this.camera);
      // Smallest hit object = most specific part (ghost child before its parent)
      const hits = this.raycaster.intersectObjects(this.getPickTargets(), false).filter(h => !this.isClippedAway(h.point));
      let best: THREE.Box3 | null = null;
      let bestVol = Infinity;
      for (const h of hits) {
        const box = ViewportManager.meshWorldBox(h.object as THREE.Mesh);
        if (!box) continue;
        const sz = box.getSize(new THREE.Vector3());
        const vol = sz.x * sz.y * sz.z;
        if (vol < bestVol) { best = box; bestVol = vol; }
      }
      cb(best ? { bounds: ViewportManager.toScadBounds(best) } : null);
    };
  }

  cancelPickOnce(): void {
    if (!this.pickOnceHandler) return;
    this.pickOnceHandler = null;
    this.measureTool.setHoverOnly(false);
    this.renderer.domElement.style.cursor = this.measureTool.isActive() ? 'crosshair' : '';
  }

  isPicking(): boolean {
    return this.pickOnceHandler !== null;
  }

  /** Dashed box showing where the selection would land (SCAD bounds), or null. */
  showSnapPreview(bounds: { min: [number, number, number]; max: [number, number, number] } | null): void {
    if (this.snapPreview) {
      this.scene.remove(this.snapPreview);
      this.snapPreview.geometry.dispose();
      (this.snapPreview.material as THREE.Material).dispose();
      this.snapPreview = null;
    }
    if (!bounds) return;
    const box = new THREE.Box3(
      new THREE.Vector3(bounds.min[0], bounds.min[2], -bounds.max[1]),
      new THREE.Vector3(bounds.max[0], bounds.max[2], -bounds.min[1]),
    );
    this.snapPreview = new THREE.Box3Helper(box, new THREE.Color(0x00d084));
    const mat = this.snapPreview.material as THREE.LineBasicMaterial;
    mat.depthTest = false;
    mat.transparent = true;
    this.snapPreview.renderOrder = 12;
    this.scene.add(this.snapPreview);
  }

  /** Meshes that represent model geometry for picking (parts + ghost children). */
  private getPickTargets(): THREE.Object3D[] {
    const targets: THREE.Object3D[] = [];
    for (const part of this.meshParts) {
      if (part.children) for (const ghost of part.children) targets.push(ghost.mesh);
      targets.push(part.mesh);
    }
    return targets;
  }

  /**
   * Raycast the model at a screen position and snap to the nearest feature
   * within `snapPx`: triangle vertex, then edge midpoint, then the centroid
   * of the coplanar face; otherwise the raw surface point. World space.
   */
  /** True when `p` (world) lies on the side removed by the active section plane. */
  private isClippedAway(p: THREE.Vector3): boolean {
    return !!this.sectionPlane && this.sectionPlane.distanceToPoint(p) < -1e-4;
  }

  /**
   * Raycast the model at a screen position and snap to a feature of the
   * flat face under the cursor: its corners (generous radius), its center
   * (largest radius), or its edge midpoints; otherwise the raw surface point.
   * Geometry cut away by a section is ignored and the section cap is
   * pickable. World space. The returned `face` (outline + snap targets)
   * lets the measure tool draw hints.
   */
  pickSnapPoint(clientX: number, clientY: number): MeasurePick | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(mouse, this.camera);
    const targets = this.getPickTargets();
    if (this.sectionCapMesh) targets.push(this.sectionCapMesh);
    if (targets.length === 0) return null;
    const hit = this.raycaster.intersectObjects(targets, false).find(i => i.face && !this.isClippedAway(i.point));
    if (!hit || !hit.face) return null;
    const mesh = hit.object as THREE.Mesh;
    // The cap's triangles have arbitrary winding: its outward normal points
    // into the removed half-space.
    const normal = mesh === this.sectionCapMesh && this.sectionPlane
      ? this.sectionPlane.normal.clone().negate()
      : hit.face.normal.clone().transformDirection(mesh.matrixWorld).normalize();

    // Outline of the whole coplanar face → real corners (where the outline
    // turns), edge midpoints and the area-weighted center.
    const fromScad = (q: [number, number, number]) => new THREE.Vector3(q[0], q[2], -q[1]);
    const { edges, center } = this.extractCoplanarBoundaryScad(hit);
    const key = (v: THREE.Vector3) => `${Math.round(v.x * 1e4)},${Math.round(v.y * 1e4)},${Math.round(v.z * 1e4)}`;
    const pts = new Map<string, THREE.Vector3>();
    const nbrs = new Map<string, string[]>();
    const segs: Array<[THREE.Vector3, THREE.Vector3]> = [];
    for (const [ea, eb] of edges) {
      const a = fromScad(ea), b = fromScad(eb);
      const ka = key(a), kb = key(b);
      if (ka === kb) continue;
      pts.set(ka, a); pts.set(kb, b);
      nbrs.set(ka, [...(nbrs.get(ka) ?? []), kb]);
      nbrs.set(kb, [...(nbrs.get(kb) ?? []), ka]);
      segs.push([a, b]);
    }
    const corners: THREE.Vector3[] = [];
    for (const [k, p] of pts) {
      const n = nbrs.get(k) ?? [];
      if (n.length !== 2) { corners.push(p); continue; }
      const u = pts.get(n[0])!.clone().sub(p).normalize();
      const w = pts.get(n[1])!.clone().sub(p).normalize();
      if (u.dot(w) > -0.9998) corners.push(p); // turns by more than ~1°
    }
    if (corners.length === 0) {
      const pos = (mesh.geometry as THREE.BufferGeometry).getAttribute('position') as THREE.BufferAttribute;
      for (const i of [hit.face.a, hit.face.b, hit.face.c]) corners.push(new THREE.Vector3().fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld));
    }
    const faceCenter = center ? fromScad(center) : null;

    // Snap radius per feature (px): the face center is the biggest target
    const candidates: Array<{ p: THREE.Vector3; kind: MeasurePick['kind']; r: number }> = [
      ...corners.map(p => ({ p, kind: 'vertex' as const, r: 18 })),
      ...segs.map(([a, b]) => ({ p: a.clone().add(b).multiplyScalar(0.5), kind: 'edge' as const, r: 12 })),
    ];
    if (faceCenter) candidates.push({ p: faceCenter, kind: 'face', r: 28 });

    const cursor = new THREE.Vector2(clientX - rect.left, clientY - rect.top);
    const toScreen = (p: THREE.Vector3) => {
      const v = p.clone().project(this.camera);
      return new THREE.Vector2((v.x * 0.5 + 0.5) * rect.width, (-v.y * 0.5 + 0.5) * rect.height);
    };
    let best: { p: THREE.Vector3; kind: MeasurePick['kind']; score: number } | null = null;
    for (const c of candidates) {
      const d = toScreen(c.p).distanceTo(cursor);
      if (d > c.r) continue;
      const score = d / c.r;
      if (!best || score < best.score) best = { p: c.p, kind: c.kind, score };
    }
    const face = { corners, center: faceCenter, edges: segs };
    return best
      ? { point: best.p.clone(), normal, kind: best.kind, face }
      : { point: hit.point.clone(), normal, kind: 'surface', face };
  }


  // Raycast the cursor, pull the hit face's normal + point, convert to SCAD
  // space, and hand off to the sketch-scaffold bridge. Used by both the
  // face-pick LMB handler and the legacy RMB handler.
  private pickFaceAndHandOff(e: MouseEvent | PointerEvent): void {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(mouse, this.camera);
    const targets: THREE.Object3D[] = [];
    for (const part of this.meshParts) {
      if (part.children && part.children.length > 0) {
        for (const ghost of part.children) targets.push(ghost.mesh);
        targets.push(part.mesh);
      } else {
        targets.push(part.mesh);
      }
    }
    if (targets.length === 0) return;
    const intersects = this.raycaster.intersectObjects(targets, false);
    const hit = intersects.find(i => i.face);
    if (!hit || !hit.face) return;
    const mesh = hit.object as THREE.Mesh;
    const normal = hit.face.normal.clone().transformDirection(mesh.matrixWorld).normalize();
    const nScad: [number, number, number] = [normal.x, -normal.z, normal.y];
    const pScad: [number, number, number] = [hit.point.x, -hit.point.z, hit.point.y];
    const { edges, center } = this.extractCoplanarBoundaryScad(hit);
    const bridge = (window as { __scadplusOpenSketchOnFace?: (n: [number, number, number], p: [number, number, number], edges?: [number, number, number][][], center?: [number, number, number] | null) => void }).__scadplusOpenSketchOnFace;
    if (bridge) bridge(nScad, pScad, edges, center);
  }

  // Right-click → "New sketch on this face": capture the picked face's world
  // normal and world hit point, hand off to the sketch editor via a window
  // bridge set up in main.ts. If no face is hit, no-op and let the browser
  // handle the context menu normally.
  private onContextMenu = (e: MouseEvent): void => {
    if (this.isViewOnly) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(mouse, this.camera);

    const targets: THREE.Object3D[] = [];
    for (const part of this.meshParts) {
      if (part.children && part.children.length > 0) {
        for (const ghost of part.children) targets.push(ghost.mesh);
        targets.push(part.mesh);
      } else {
        targets.push(part.mesh);
      }
    }
    if (targets.length === 0) return;
    const intersects = this.raycaster.intersectObjects(targets, false);
    const hit = intersects.find(i => i.face);
    if (!hit || !hit.face) return;

    e.preventDefault();

    // Local face normal → world space
    const mesh = hit.object as THREE.Mesh;
    const normal = hit.face.normal.clone();
    normal.transformDirection(mesh.matrixWorld).normalize();

    // Viewport uses Y-up (rotateX(-PI/2) on the whole scene container to
    // convert OpenSCAD's Z-up). Convert the world-space three.js point/normal
    // back to SCAD coordinates by undoing that rotation: (x, y, z)_three =
    // (x, z, -y)_scad  →  (x, -z, y)_scad.
    const nScad: [number, number, number] = [normal.x, -normal.z, normal.y];
    const pScad: [number, number, number] = [hit.point.x, -hit.point.z, hit.point.y];

    const { edges, center } = this.extractCoplanarBoundaryScad(hit);
    const bridge = (window as { __scadplusOpenSketchOnFace?: (n: [number, number, number], p: [number, number, number], edges?: [number, number, number][][], center?: [number, number, number] | null) => void }).__scadplusOpenSketchOnFace;
    if (bridge) bridge(nScad, pScad, edges, center);
  };

  private onPointerUp = (e: PointerEvent): void => {
    // If a meaningful gizmo drag just ended, skip click handling
    if (this.gizmoDragOccurred) {
      this.gizmoDragOccurred = false;
      return;
    }

    const dx = e.clientX - this.pointerDownPos.x;
    const dy = e.clientY - this.pointerDownPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const elapsed = performance.now() - this.pointerDownTime;

    // Only treat as click if small movement and short duration
    if (dist < 5 && elapsed < 300) {
      if (this.pickOnceHandler) { this.pickOnceHandler(e); return; }
      if (this.measureTool.handleClick(e)) return;
      this.handleClick(e);
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    // Face-pick preview update (only does work when faceHoverMode is on).
    this.updateFaceHighlight(e);
    this.measureTool.handleHover(e);
    const rect = this.renderer.domElement.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const pad = 4;
    const hudSize = Math.min(180, Math.floor(Math.min(rect.width, rect.height) * 0.22));
    const hudLeft = rect.width - hudSize - pad;
    const hudTop = pad;
    const inHud = hudSize > 20 && cx >= hudLeft && cx <= hudLeft + hudSize && cy >= hudTop && cy <= hudTop + hudSize;
    if (inHud) {
      const hx = ((cx - hudLeft) / hudSize) * 2 - 1;
      const hy = -((cy - hudTop) / hudSize) * 2 + 1;
      this.hudScene.updateMatrixWorld();
      this.hudRaycaster.setFromCamera(new THREE.Vector2(hx, hy), this.hudCamera);
      let hit: THREE.Intersection | null = null;
      let cubeHalf = 0.35;
      const innerHits = this.hudRaycaster.intersectObject(this.viewCubeMesh, false);
      if (innerHits.length > 0 && innerHits[0].face) {
        hit = innerHits[0]; cubeHalf = 0.35;
      } else {
        const proxyHits = this.hudRaycaster.intersectObject(this.viewCubeProxy, false);
        if (proxyHits.length > 0 && proxyHits[0].face) {
          hit = proxyHits[0]; cubeHalf = 0.7;
        }
      }
      if (hit && hit.face) {
        const zone = this.classifyHitZone(hit.point, cubeHalf, hit.face.materialIndex);
        // Different cursor per zone: pointer=face, crosshair=edge, cell=corner
        this.renderer.domElement.style.cursor =
          zone.type === 'corner' ? 'cell' : zone.type === 'edge' ? 'crosshair' : 'pointer';
      } else {
        this.renderer.domElement.style.cursor = '';
      }
    } else {
      this.renderer.domElement.style.cursor = '';
    }
  };

  private handleClick(e: PointerEvent): void {
    const rect = this.renderer.domElement.getBoundingClientRect();

    // Check if click is inside HUD viewport region (top-right corner)
    {
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const pad = 4;
      const hudSize = Math.min(180, Math.floor(Math.min(rect.width, rect.height) * 0.22));
      if (hudSize > 20) {
        const hudLeft = rect.width - hudSize - pad;
        const hudTop = pad;
        if (cx >= hudLeft && cx <= hudLeft + hudSize && cy >= hudTop && cy <= hudTop + hudSize) {
          const hx = ((cx - hudLeft) / hudSize) * 2 - 1;
          const hy = -((cy - hudTop) / hudSize) * 2 + 1;
          this.hudScene.updateMatrixWorld();
          this.hudRaycaster.setFromCamera(new THREE.Vector2(hx, hy), this.hudCamera);

          const viewMap: Array<'right' | 'left' | 'top' | 'bottom' | 'front' | 'back'> = [
            'right', 'left', 'top', 'bottom', 'front', 'back',
          ];
          const faceDirs = [
            new THREE.Vector3(1, 0, 0),   new THREE.Vector3(-1, 0, 0),
            new THREE.Vector3(0, 1, 0),   new THREE.Vector3(0, -1, 0),
            new THREE.Vector3(0, 0, 1),   new THREE.Vector3(0, 0, -1),
          ];

          // Try inner cube first, then proxy
          let hit: THREE.Intersection | null = null;
          let cubeHalf = 0.35;
          const innerHits = this.hudRaycaster.intersectObject(this.viewCubeMesh, false);
          if (innerHits.length > 0 && innerHits[0].face) {
            hit = innerHits[0];
            cubeHalf = 0.35;
          } else {
            const proxyHits = this.hudRaycaster.intersectObject(this.viewCubeProxy, false);
            if (proxyHits.length > 0 && proxyHits[0].face) {
              hit = proxyHits[0];
              cubeHalf = 0.7;
            }
          }

          if (hit && hit.face) {
            const zone = this.classifyHitZone(hit.point, cubeHalf, hit.face.materialIndex);
            if (zone.type === 'face') {
              this.setStandardView(viewMap[zone.faceIndex]);
            } else if (zone.type === 'edge') {
              this.setStandardView(viewMap[zone.adjacentIndex]);
            } else {
              // Corner: isometric direction from sum of 3 face normals
              const dir = new THREE.Vector3()
                .add(faceDirs[zone.faceIndex])
                .add(faceDirs[zone.adj1Index])
                .add(faceDirs[zone.adj2Index])
                .normalize();
              this.navigateToView(dir);
            }
          }
          return;
        }
      }
    }

    // In view-only mode, allow view cube clicks above but skip scene selection
    if (this.isViewOnly) return;
    // Face-pick mode: LMB on a 3D face captures it and hands off to the
    // sketch scaffold bridge. No normal scene selection while picking.
    if (this.faceHoverMode) {
      this.pickFaceAndHandOff(e);
      return;
    }

    const mouse = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );

    this.raycaster.setFromCamera(mouse, this.camera);

    if (this.meshParts.length === 0) return;

    // Collect all raycast targets: visible meshes + invisible ghost meshes
    type HitCandidate = { part: MeshPart; ghost?: GhostPart };
    const candidates: Array<{ obj: THREE.Object3D; candidate: HitCandidate }> = [];

    for (const part of this.meshParts) {
      if (part.children && part.children.length > 0) {
        // Boolean group: ghost meshes for children
        for (const ghost of part.children) {
          candidates.push({ obj: ghost.mesh, candidate: { part, ghost } });
        }
        // Also add the combined mesh as fallback (select group)
        candidates.push({ obj: part.mesh, candidate: { part } });
      } else {
        // Simple part: raycast against visible mesh
        candidates.push({ obj: part.mesh, candidate: { part } });
      }
    }

    const objects = candidates.map(c => c.obj);
    const intersects = this.raycaster.intersectObjects(objects, false);

    if (intersects.length > 0) {
      // Collect ALL raycast hits with distance metadata.
      // No distance threshold — nested children inside cut openings can be
      // much deeper than the parent surface, so we need all hits for cycling.
      const closestDist = intersects[0].distance;
      type RankedHit = { candidate: HitCandidate; distance: number; vertexCount: number };
      const rawHits: RankedHit[] = [];
      for (const inter of intersects) {
        const hit = candidates.find(c => c.obj === inter.object);
        if (hit) {
          const vCount = hit.candidate.ghost
            ? ((inter.object as THREE.Mesh).geometry.getAttribute('position')?.count ?? Infinity)
            : Infinity;
          rawHits.push({ candidate: hit.candidate, distance: inter.distance, vertexCount: vCount });
        }
      }

      // Sort: at similar depth (within 0.1), prefer smallest ghost (deepest child);
      // otherwise sort front-to-back by distance so cycling goes near → far.
      rawHits.sort((a, b) => {
        const distDiff = a.distance - b.distance;
        if (Math.abs(distDiff) <= 0.1) {
          // Same depth band: ghosts before parent meshes, smaller vertex count first
          if (a.candidate.ghost && !b.candidate.ghost) return -1;
          if (!a.candidate.ghost && b.candidate.ghost) return 1;
          return a.vertexCount - b.vertexCount;
        }
        return distDiff;
      });

      // Deduplicate: each ghost/part should appear only once (first occurrence wins)
      const seen = new Set<object>();
      const allHits: HitCandidate[] = [];
      for (const rh of rawHits) {
        const key: object = rh.candidate.ghost ?? rh.candidate.part;
        if (!seen.has(key)) {
          seen.add(key);
          allHits.push(rh.candidate);
        }
      }

      // Find the currently selected object in the hit list to enable click-cycling
      let currentIndex = -1;
      for (let i = 0; i < allHits.length; i++) {
        const h = allHits[i];
        if (h.ghost && h.ghost === this.selectedGhost) {
          currentIndex = i;
          break;
        }
        if (!h.ghost && h.part === this.selectedPart && !this.selectedGhost) {
          currentIndex = i;
          break;
        }
      }

      const selectIndex = currentIndex >= 0
        ? (currentIndex + 1) % allHits.length
        : 0;

      const selected = allHits[selectIndex];
      if (selected) {
        if (selected.ghost) {
          this.selectGhostChild(selected.part, selected.ghost);
        } else {
          this.selectPart(selected.part);
        }
      }
    } else {
      this.deselectAll();
    }
  }

  private selectPart(part: MeshPart): void {
    this.clearGhostSelection();
    this.selectedPart = part;
    this.selectionOutlinePass.selectedObjects = [part.mesh];
    this.transformControls.attach(part.mesh);
    if (this.modeChangeCallback) {
      this.modeChangeCallback(this.transformControls.mode, true);
    }
    // Fire child-selected callback so param panel shows the full module chain
    if (this.childSelectedCallback) {
      this.childSelectedCallback(part.from, part.to);
    }
  }

  private selectGhostChild(parentPart: MeshPart, ghost: GhostPart): void {
    if (this.selectedGhost === ghost) return;

    this.clearGhostSelection();
    this.selectedPart = parentPart;
    this.selectedGhost = ghost;

    // Show ghost geometry as semi-transparent highlight — gizmo attaches here
    this.ghostHighlight = new THREE.Mesh(ghost.mesh.geometry, this.ghostHighlightMaterial);
    this.ghostHighlight.position.copy(ghost.center);
    this.ghostHighlight.renderOrder = 2;
    this.scene.add(this.ghostHighlight);

    this.selectionOutlinePass.selectedObjects = [this.ghostHighlight];
    this.transformControls.attach(this.ghostHighlight);

    if (this.modeChangeCallback) {
      this.modeChangeCallback(this.transformControls.mode, true);
    }
    // Fire child-selected callback for parameter editing
    if (this.childSelectedCallback) {
      this.childSelectedCallback(ghost.from, ghost.to);
    }
  }

  private clearGhostSelection(): void {
    if (this.ghostHighlight) {
      this.scene.remove(this.ghostHighlight);
      this.ghostHighlight = null;
    }
    this.selectedGhost = null;
  }

  deselectAll(): void {
    this.clearGhostSelection();
    this.selectedPart = null;
    this.selectionOutlinePass.selectedObjects = [];
    this.transformControls.detach();
    this.dimensionOverlay.clear();
    if (this.modeChangeCallback) {
      this.modeChangeCallback(this.transformControls.mode, false);
    }
    if (this.selectionClearedCallback) {
      this.selectionClearedCallback();
    }
  }

  // Select a mesh part by source offset (used to restore selection after code changes)
  selectByOffset(offset: number): boolean {
    for (const part of this.meshParts) {
      // Check ghost children first (more specific range)
      if (part.children) {
        for (const ghost of part.children) {
          if (offset >= ghost.from.offset && offset < ghost.to.offset) {
            this.selectGhostChild(part, ghost);
            return true;
          }
        }
      }
      if (offset >= part.from.offset && offset < part.to.offset) {
        this.selectPart(part);
        return true;
      }
    }
    return false;
  }

  setViewOnly(enabled: boolean): void {
    this.isViewOnly = enabled;
    if (enabled) {
      this.transformControls.detach();
      this.transformControls.enabled = false;
      this.dimensionOverlay.clear();
    }
  }

  setTransformMode(mode: 'translate' | 'rotate' | 'scale'): void {
    this.transformControls.setMode(mode);
    // Hide dimensions in rotate mode, show in other modes
    this.dimensionOverlay.setVisible(mode !== 'rotate');
    if (this.modeChangeCallback) {
      this.modeChangeCallback(mode, this.selectedPart !== null);
    }
  }

  getTransformMode(): string {
    return this.transformControls.mode;
  }

  onTransformEnd(callback: (event: TransformEndEvent) => void): void {
    this.transformEndCallback = callback;
  }

  onModeChange(callback: (mode: string, selected: boolean) => void): void {
    this.modeChangeCallback = callback;
  }

  onChildSelected(callback: (from: SourcePosition, to: SourcePosition) => void): void {
    this.childSelectedCallback = callback;
  }

  onSelectionCleared(callback: () => void): void {
    this.selectionClearedCallback = callback;
  }

  onTransformLive(callback: () => void): void {
    this.transformLiveCallback = callback;
  }

  setSnapSettings(enabled: boolean, step: number): void {
    this.dimensionOverlay.setSnapSettings(enabled, step);
    this.snapEnabled = enabled;
    this.snapStep = Math.max(0.01, step);
  }

  /** Snap a value to the nearest grid step (same algorithm as DimensionOverlay). */
  private snapValue(value: number): number {
    if (!this.snapEnabled || this.snapStep <= 0) return value;
    const precision = (this.snapStep.toString().split('.')[1] || '').length;
    const scale = Math.pow(10, precision);
    const intStep = Math.round(this.snapStep * scale);
    const intValue = Math.round(value * scale);
    return Math.round(intValue / intStep) * intStep / scale;
  }

  showDimensions(setup: DimensionSetup): void {
    this.dimensionOverlay.show(setup);
  }

  clearDimensions(): void {
    this.dimensionOverlay.clear();
  }

  onDimensionEdit(cb: (from: number, to: number, value: string) => void): void {
    this.dimensionEditCallback = cb;
  }

  setAxisLock(scadX: boolean, scadY: boolean, scadZ: boolean): void {
    this.dimensionOverlay.setAxisLock(scadX, scadY, scadZ);
  }

  isDimensionDragging(): boolean {
    return this.dimensionOverlay.isDragging();
  }

  /** Toggle keyboard axis constraint during drag. Same axis pressed again clears it. */
  setDragAxisConstraint(worldAxis: 'x' | 'y' | 'z'): void {
    const current = this.dimensionOverlay.getKeyboardAxisConstraint();
    this.dimensionOverlay.setKeyboardAxisConstraint(current === worldAxis ? null : worldAxis);
  }

  /** Returns the bounding box and center of the currently selected mesh, or null. */
  getSelectedBBox(): { bbox: THREE.Box3; center: THREE.Vector3 } | null {
    const mesh = this.selectedGhost
      ? (this.ghostHighlight ?? this.selectedGhost.mesh)
      : this.selectedPart?.mesh ?? null;
    if (!mesh) return null;

    const geo = mesh.geometry;
    geo.computeBoundingBox();
    if (!geo.boundingBox) return null;

    // Transform bbox to world space (mesh position offsets the centered geometry)
    const bbox = geo.boundingBox.clone();
    bbox.translate(mesh.position);

    const center = new THREE.Vector3();
    bbox.getCenter(center);

    return { bbox, center };
  }

  /** Returns the mesh Euler rotation (radians) of the selected object, or null. */
  getSelectedMeshRotation(): THREE.Euler | null {
    const mesh = this.selectedGhost
      ? (this.ghostHighlight ?? this.selectedGhost.mesh)
      : this.selectedPart?.mesh ?? null;
    return mesh ? mesh.rotation.clone() : null;
  }

  /** Returns the source offset of the currently selected part or ghost, or null. */
  getSelectedOffset(): number | null {
    if (this.selectedGhost) return this.selectedGhost.from.offset;
    if (this.selectedPart) return this.selectedPart.from.offset;
    return null;
  }

  /** Returns the source range (from, to offsets) of the currently selected part or ghost, or null. */
  getSelectedRange(): { from: number; to: number } | null {
    if (this.selectedGhost) {
      return { from: this.selectedGhost.from.offset, to: this.selectedGhost.to.offset };
    }
    if (this.selectedPart) {
      return { from: this.selectedPart.from.offset, to: this.selectedPart.to.offset };
    }
    return null;
  }

  /** Source offset + module instance of the selection (stable across re-renders). */
  getSelectedKey(): { offset: number; instance?: string } | null {
    if (this.selectedGhost) return { offset: this.selectedGhost.from.offset, instance: this.selectedGhost.instance };
    if (this.selectedPart) return { offset: this.selectedPart.from.offset };
    return null;
  }

  /** Re-select a part or ghost child matching the given source offset —
   *  preferring the given module instance when several uses share it. */
  selectBySourceOffset(offset: number, instance?: string): boolean {
    if (instance !== undefined) {
      for (const part of this.meshParts) {
        const ghost = part.children?.find(g => g.from.offset === offset && g.instance === instance);
        if (ghost) { this.selectGhostChild(part, ghost); return true; }
      }
    }
    for (const part of this.meshParts) {
      // Check ghost children first
      if (part.children) {
        for (const ghost of part.children) {
          if (ghost.from.offset === offset) {
            this.selectGhostChild(part, ghost);
            return true;
          }
        }
      }
      // Check the part itself
      if (part.from.offset === offset) {
        this.selectPart(part);
        return true;
      }
    }
    return false;
  }

  /** Select the innermost (tightest-range) part or ghost whose source range
   *  contains the given cursor offset. Lets the user pick deeply-nested
   *  children — which are hard to click — by just moving the editor caret
   *  onto their line in the source. */
  selectByCursorOffset(offset: number): boolean {
    let best: { part: MeshPart; ghost?: GhostPart; span: number } | null = null;
    for (const part of this.meshParts) {
      // Ghost children first — usually have tighter ranges
      if (part.children) {
        for (const ghost of part.children) {
          if (offset >= ghost.from.offset && offset <= ghost.to.offset) {
            const span = ghost.to.offset - ghost.from.offset;
            if (!best || span < best.span) best = { part, ghost, span };
          }
        }
      }
      // Part itself
      if (offset >= part.from.offset && offset <= part.to.offset) {
        const span = part.to.offset - part.from.offset;
        if (!best || span < best.span) best = { part, span };
      }
    }
    if (!best) return false;
    if (best.ghost) {
      this.selectGhostChild(best.part, best.ghost);
    } else {
      this.selectPart(best.part);
    }
    return true;
  }

  /** Swap Y/Z handle colors on the TransformControls gizmo to match OpenSCAD conventions:
   *  Three.js Y (up) → Blue (SCAD Z), Three.js Z (forward) → Green (SCAD Y). */
  private recolorGizmoForSCAD(): void {
    const internalGizmo = (this.transformControls as any)._gizmo;
    if (!internalGizmo) return;
    for (const mode of ['translate', 'rotate', 'scale']) {
      const modeObj = internalGizmo.gizmo[mode];
      if (modeObj) {
        modeObj.traverse((child: any) => {
          if (!child.material?.color) return;
          if (child.name === 'Y') child.material.color.setHex(0x4488ff);
          else if (child.name === 'Z') child.material.color.setHex(0x44cc44);
        });
      }
      const pickerObj = internalGizmo.picker[mode];
      if (pickerObj) {
        pickerObj.traverse((child: any) => {
          if (!child.material?.color) return;
          if (child.name === 'Y') child.material.color.setHex(0x4488ff);
          else if (child.name === 'Z') child.material.color.setHex(0x44cc44);
        });
      }
    }
    this.setupRotationHandles();
  }

  /**
   * Set up Bambu Studio-style rotation handles.
   * Elements are added to a separate overlay group on the helper (NOT as direct
   * children of gizmo['rotate'], because TC overrides position/scale/visibility
   * on those every frame).
   */
  private setupRotationHandles(): void {
    const helper = this.transformControls.getHelper();

    // Create our overlay container — a child of the helper, not of gizmo['rotate']
    const overlay = new THREE.Group();
    overlay.name = 'rotationOverlay';
    overlay.visible = false; // hidden until rotate mode
    helper.add(overlay);
    this.rotationOverlay = overlay;

    // Clear TC's own gizmo children for 'rotate' — our overlay replaces them,
    // and removing them prevents TC from drawing a yellow highlight on hover
    const internalGizmo = (this.transformControls as any)._gizmo;
    const rotateGizmo = internalGizmo?.gizmo?.['rotate'];
    if (rotateGizmo) {
      for (const child of [...rotateGizmo.children]) child.removeFromParent();
    }

    // Make TC's rotation picker torus materials truly non-rendering
    // (they still work for raycasting — colorWrite only affects rendering)
    const rotatePicker = internalGizmo?.picker?.['rotate'];
    if (rotatePicker) {
      for (const child of rotatePicker.children) {
        const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
        if (mat) { mat.colorWrite = false; mat.depthWrite = false; }
      }
    }

    // Remove helper AXIS line — our overlay provides visual feedback instead
    const rotateHelper = internalGizmo?.helper?.['rotate'];
    if (rotateHelper) {
      for (const child of [...rotateHelper.children]) child.removeFromParent();
    }

    // TC uses TorusGeometry radius = 0.5 for rotation circles
    const R = 0.5;

    const axes: Array<{
      name: string; color: number; handleAngle: number;
      buildPoint: (r: number, a: number) => THREE.Vector3;
      tangent: THREE.Vector3; radial: THREE.Vector3; normal: THREE.Vector3;
    }> = [
      {
        name: 'X', color: 0xff0000, handleAngle: 0,
        buildPoint: (r, a) => new THREE.Vector3(0, r * Math.cos(a), r * Math.sin(a)),
        tangent: new THREE.Vector3(0, 0, 1), radial: new THREE.Vector3(0, 1, 0),
        normal: new THREE.Vector3(1, 0, 0),
      },
      {
        name: 'Y', color: 0x4488ff, handleAngle: Math.PI,
        buildPoint: (r, a) => new THREE.Vector3(r * Math.cos(a), 0, r * Math.sin(a)),
        tangent: new THREE.Vector3(0, 0, 1), radial: new THREE.Vector3(1, 0, 0),
        normal: new THREE.Vector3(0, 1, 0),
      },
      {
        name: 'Z', color: 0x44cc44, handleAngle: -Math.PI / 2,
        buildPoint: (r, a) => new THREE.Vector3(r * Math.cos(a), r * Math.sin(a), 0),
        tangent: new THREE.Vector3(0, 1, 0), radial: new THREE.Vector3(1, 0, 0),
        normal: new THREE.Vector3(0, 0, 1),
      },
    ];

    for (const axis of axes) {
      const handleCenter = axis.buildPoint(R, 0);
      this.rotationHandlePositions.set(axis.name, handleCenter.clone());
      // Rotate tangent/radial to match the handle's angle on the circle
      const t = axis.tangent.clone().applyAxisAngle(axis.normal, axis.handleAngle);
      const rd = axis.radial.clone().applyAxisAngle(axis.normal, axis.handleAngle);
      const n = axis.normal;

      // --- Full circle for this axis (TC only draws half-circles) ---
      const circlePts: number[] = [];
      for (let deg = 0; deg < 360; deg += 3) {
        const a1 = THREE.MathUtils.degToRad(deg);
        const a2 = THREE.MathUtils.degToRad(deg + 3);
        const p1 = axis.buildPoint(R, a1);
        const p2 = axis.buildPoint(R, a2);
        circlePts.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
      }
      const circleGeom = new THREE.BufferGeometry();
      circleGeom.setAttribute('position', new THREE.Float32BufferAttribute(circlePts, 3));
      const circleLine = new THREE.LineSegments(circleGeom, new THREE.LineBasicMaterial({
        color: axis.color, depthTest: false, transparent: true, opacity: 0.7,
      }));
      circleLine.name = axis.name;
      circleLine.renderOrder = Infinity;
      overlay.add(circleLine);

      // --- Handle outside circle: left cone – cube – right cone (in a group for rotation) ---
      const handleGroup = new THREE.Group();
      handleGroup.name = `${axis.name}_handle`;
      handleGroup.renderOrder = Infinity;

      // Position handle center just outside the circle at this axis's handle angle
      const handleOut = axis.buildPoint(R * 1.14, axis.handleAngle);
      // Update hover detection position to the outer handle
      this.rotationHandlePositions.set(axis.name, handleOut.clone());

      const coneRadius = 0.035;
      const coneHeight = 0.10;
      const cubeHalf = 0.03;
      const gap = 0.02;
      const handleMat = new THREE.MeshBasicMaterial({
        color: axis.color, depthTest: false, side: THREE.DoubleSide,
      });

      // Center cube
      const boxGeom = new THREE.BoxGeometry(cubeHalf * 2, cubeHalf * 2, cubeHalf * 2);
      // Bake orientation: align box axes to (tangent, radial, normal)
      const boxMatrix = new THREE.Matrix4().makeBasis(t, rd, n);
      boxGeom.applyMatrix4(boxMatrix);
      const cubeMesh = new THREE.Mesh(boxGeom, handleMat);
      cubeMesh.position.copy(handleOut);
      cubeMesh.name = axis.name;
      cubeMesh.renderOrder = Infinity;
      handleGroup.add(cubeMesh);

      // Two cones (tips pointing outward along tangent, one each direction)
      for (const sign of [1, -1]) {
        const coneGeom = new THREE.ConeGeometry(coneRadius, coneHeight, 12);
        // ConeGeometry tip is at +Y by default. Rotate to point along sign*tangent.
        const targetDir = t.clone().multiplyScalar(sign);
        const q = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, 1, 0), targetDir,
        );
        coneGeom.applyQuaternion(q);
        const coneMesh = new THREE.Mesh(coneGeom, handleMat);
        // Position: cube edge + half cone height along tangent
        coneMesh.position.copy(handleOut).addScaledVector(t, sign * (cubeHalf + gap + coneHeight / 2));
        coneMesh.name = axis.name;
        coneMesh.renderOrder = Infinity;
        handleGroup.add(coneMesh);
      }

      overlay.add(handleGroup);
      this.rotationHandleGroups.set(axis.name, handleGroup);

      // --- Detail group (hidden by default, shown on hover/drag) ---
      const detailGroup = new THREE.Group();
      detailGroup.name = `${axis.name}_detail`;
      detailGroup.visible = false;

      // Outer ring 5° tick marks (short ticks, longer at 45° intervals)
      const finePts: number[] = [];
      for (let i = 0; i < 72; i++) {
        const angle = i * Math.PI / 36;
        const isCoarse = i % 9 === 0;
        const outerPt = axis.buildPoint(R * 1.04, angle);
        const innerPt = axis.buildPoint(isCoarse ? R * 0.82 : R * 0.92, angle);
        finePts.push(outerPt.x, outerPt.y, outerPt.z, innerPt.x, innerPt.y, innerPt.z);
      }
      const fineGeom = new THREE.BufferGeometry();
      fineGeom.setAttribute('position', new THREE.Float32BufferAttribute(finePts, 3));
      const fineLines = new THREE.LineSegments(fineGeom, new THREE.LineBasicMaterial({
        color: 0xffffff, depthTest: false, transparent: true, opacity: 0.45,
      }));
      fineLines.renderOrder = Infinity;
      detailGroup.add(fineLines);

      // Inner circle at R * 0.65
      const innerR = R * 0.65;
      const innerCirclePts: number[] = [];
      for (let deg = 0; deg < 360; deg += 3) {
        const a1 = THREE.MathUtils.degToRad(deg);
        const a2 = THREE.MathUtils.degToRad(deg + 3);
        const p1 = axis.buildPoint(innerR, a1);
        const p2 = axis.buildPoint(innerR, a2);
        innerCirclePts.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
      }
      const innerCircleGeom = new THREE.BufferGeometry();
      innerCircleGeom.setAttribute('position', new THREE.Float32BufferAttribute(innerCirclePts, 3));
      const innerLine = new THREE.LineSegments(innerCircleGeom, new THREE.LineBasicMaterial({
        color: 0xffffff, depthTest: false, transparent: true, opacity: 0.25,
      }));
      innerLine.renderOrder = Infinity;
      detailGroup.add(innerLine);

      // 45° tick marks on inner circle
      const innerTickPts: number[] = [];
      for (let i = 0; i < 8; i++) {
        const angle = i * Math.PI / 4;
        const inner = axis.buildPoint(innerR * 0.82, angle);
        const outer = axis.buildPoint(innerR * 1.04, angle);
        innerTickPts.push(inner.x, inner.y, inner.z, outer.x, outer.y, outer.z);
      }
      const innerTickGeom = new THREE.BufferGeometry();
      innerTickGeom.setAttribute('position', new THREE.Float32BufferAttribute(innerTickPts, 3));
      const innerTickLines = new THREE.LineSegments(innerTickGeom, new THREE.LineBasicMaterial({
        color: 0xffffff, depthTest: false, transparent: true, opacity: 0.5,
      }));
      innerTickLines.renderOrder = Infinity;
      detailGroup.add(innerTickLines);

      overlay.add(detailGroup);
      this.rotationDetailGroups.set(axis.name, detailGroup);

      // --- Rotation angle indicator (needle, hidden until drag) ---
      const indicatorGeom = new THREE.BufferGeometry();
      indicatorGeom.setAttribute('position', new THREE.Float32BufferAttribute([
        0, 0, 0, ...axis.buildPoint(R * 1.08, 0).toArray(),
      ], 3));
      const indicator = new THREE.Line(indicatorGeom, new THREE.LineBasicMaterial({
        color: 0xffffff, depthTest: false,
      }));
      indicator.name = `${axis.name}_indicator`;
      indicator.visible = false;
      indicator.renderOrder = Infinity;
      overlay.add(indicator);
      this.rotationIndicators.set(axis.name, indicator);
    }
  }

  /** Check if mouse hovers over a rotation handle and show/hide detail rings. */
  private updateRotationHandleHover(
    mouseX: number, mouseY: number, containerW: number, containerH: number,
  ): void {
    if (this.transformControls.mode !== 'rotate' || this.draggingRotation) return;
    if (!this.rotationOverlay || !this.rotationOverlay.visible) return;

    this.rotationOverlay.updateMatrixWorld(true);

    let closest: string | null = null;
    let closestDist = 30; // 30px threshold

    for (const [name, localPos] of this.rotationHandlePositions) {
      const worldPos = localPos.clone().applyMatrix4(this.rotationOverlay.matrixWorld);
      const ndc = worldPos.project(this.camera);
      const px = (ndc.x + 1) / 2 * containerW;
      const py = (-ndc.y + 1) / 2 * containerH;
      const dist = Math.sqrt((mouseX - px) ** 2 + (mouseY - py) ** 2);
      if (dist < closestDist) {
        closestDist = dist;
        closest = name;
      }
    }

    if (closest !== this.hoveredRotationAxis) {
      this.hoveredRotationAxis = closest;
      if (closest) {
        // Show detail for hovered axis, hide other axes entirely
        for (const child of this.rotationOverlay.children) {
          const n = child.name;
          if (n === closest || n === `${closest}_detail` || n === `${closest}_handle`) {
            child.visible = true;
          } else if (['X', 'Y', 'Z'].includes(n) || n.endsWith('_detail') || n.endsWith('_handle')) {
            child.visible = false;
          }
          // _indicator children stay as-is (controlled by drag logic)
        }
      } else {
        // No hover — show all axes, hide detail groups
        for (const child of this.rotationOverlay.children) {
          const n = child.name;
          if (n.endsWith('_detail')) {
            child.visible = false;
          } else if (!n.endsWith('_indicator')) {
            child.visible = true;
          }
        }
      }
    }
  }

  /** Show detail for active axis and hide inactive axes during rotation drag. */
  private activateRotationAxis(activeAxis: string): void {
    if (!this.rotationOverlay) return;
    // Show only the active axis — hide everything else
    for (const child of this.rotationOverlay.children) {
      const n = child.name;
      if (n === activeAxis || n === `${activeAxis}_detail` || n === `${activeAxis}_handle`) {
        child.visible = true;
      } else if (['X', 'Y', 'Z'].includes(n) || n.endsWith('_detail') || n.endsWith('_handle')) {
        child.visible = false;
      }
      // _indicator children stay as-is (controlled by drag logic)
    }
  }

  /** Restore all rotation axes visibility after drag ends. */
  private deactivateRotationAxes(): void {
    if (!this.rotationOverlay) return;
    for (const child of this.rotationOverlay.children) {
      const name = child.name;
      if (name.endsWith('_detail')) {
        child.visible = false;
      } else if (name.endsWith('_indicator')) {
        child.visible = false;
      } else {
        child.visible = true;
      }
    }
    // Reset handle group rotations
    for (const group of this.rotationHandleGroups.values()) {
      group.quaternion.identity();
    }
  }

  /** Compute a unit direction from gizmo center to the mouse's projection on the rotation plane. */
  private computeCircleDir(axisName: string): THREE.Vector3 | null {
    const helper = this.transformControls.getHelper();
    const gizmoCenter = new THREE.Vector3();
    helper.getWorldPosition(gizmoCenter);

    const normals: Record<string, THREE.Vector3> = {
      X: new THREE.Vector3(1, 0, 0),
      Y: new THREE.Vector3(0, 1, 0),
      Z: new THREE.Vector3(0, 0, 1),
    };
    const normal = normals[axisName];
    if (!normal) return null;

    // Cast ray through current mouse position
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(this.lastMouseNDC, this.camera);

    // Use eye-facing plane to avoid degenerate edge-on views
    const eye = new THREE.Vector3();
    (this.camera as THREE.PerspectiveCamera).getWorldPosition(eye);
    const eyeDir = eye.clone().sub(gizmoCenter).normalize();

    const eyePlane = new THREE.Plane().setFromNormalAndCoplanarPoint(eyeDir, gizmoCenter);
    const hit = new THREE.Vector3();
    if (!raycaster.ray.intersectPlane(eyePlane, hit)) return null;

    // Project onto the rotation plane (remove component along axis normal)
    const dir = hit.sub(gizmoCenter);
    dir.addScaledVector(normal, -dir.dot(normal));
    if (dir.length() < 1e-6) return null;
    return dir.normalize();
  }

  private updateRotationIndicator(_rotation: THREE.Euler): void {
    const tc = this.transformControls as any;
    const activeAxis = tc.axis as string | null;
    if (!activeAxis || !this.rotationDragStartDir) return;

    // Use TC's actual rotation angle + axis to rotate the start direction
    const rotAngle: number = tc.rotationAngle;
    const rotAxis: THREE.Vector3 = tc.rotationAxis;
    if (!rotAxis || rotAxis.lengthSq() < 1e-10) return;

    const q = new THREE.Quaternion().setFromAxisAngle(rotAxis.clone().normalize(), rotAngle);
    const currentDir = this.rotationDragStartDir.clone().applyQuaternion(q);

    const R = 0.5 * 1.08;
    const end = currentDir.multiplyScalar(R);

    // Show only the active indicator
    for (const [name, ind] of this.rotationIndicators) {
      if (name === activeAxis) {
        ind.visible = true;
        const pos = ind.geometry.getAttribute('position') as THREE.BufferAttribute;
        pos.setXYZ(1, end.x, end.y, end.z);
        pos.needsUpdate = true;
      } else {
        ind.visible = false;
      }
    }

    // Rotate the active handle group around the circle
    const handleGroup = this.rotationHandleGroups.get(activeAxis);
    if (handleGroup) {
      handleGroup.quaternion.copy(q);
    }
  }

  /** Remove non-axis rotation gizmo elements (E = screen ring, XYZE = trackball).
   *  Only removes direct children by exact name — avoids touching nested custom elements. */
  private cleanupRotationGizmo(): void {
    const internalGizmo = (this.transformControls as any)._gizmo;
    if (!internalGizmo) return;
    const removeNames = ['E', 'XYZE'];
    for (const group of [internalGizmo.gizmo['rotate'], internalGizmo.picker['rotate']]) {
      if (!group) continue;
      const toRemove: THREE.Object3D[] = [];
      for (const child of [...group.children]) {
        if (removeNames.includes(child.name)) {
          toRemove.push(child);
        }
      }
      for (const obj of toRemove) obj.removeFromParent();
    }
  }

  private handleResize(): void {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    if (width === 0 || height === 0) return;
    if (this.camera instanceof THREE.PerspectiveCamera) {
      this.camera.aspect = width / height;
    } else {
      const ortho = this.camera as THREE.OrthographicCamera;
      const aspect = width / height;
      const halfH = ortho.top;
      ortho.left = -halfH * aspect;
      ortho.right = halfH * aspect;
    }
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.composer.setSize(width, height);
    this.grid.setResolution(width, height);
  }

  /** Set near/far clipping planes appropriate for the current camera type. */
  private updateNearFar(distance: number, radius?: number): void {
    if (this.camera instanceof THREE.OrthographicCamera) {
      const f = Math.max(2000, distance * 4);
      this.camera.near = -f;
      this.camera.far = f;
    } else {
      this.camera.near = Math.max(0.01, distance * 0.01);
      this.camera.far = Math.max(2000, (radius !== undefined ? distance + radius : distance) * 10);
    }
  }

  /** Sync our rotation overlay position/scale/visibility with TC's picker handles.
   *  We use picker (not gizmo) because gizmo['rotate'] children are cleared to prevent
   *  TC's yellow highlight. Picker children still get position/scale set by TC every frame. */
  private syncRotationOverlay(): void {
    if (!this.rotationOverlay) return;
    const internalGizmo = (this.transformControls as any)._gizmo;
    const rotatePicker = internalGizmo?.picker?.['rotate'];
    const isRotate = this.transformControls.mode === 'rotate' && this.transformControls.object;
    if (!isRotate || !rotatePicker || rotatePicker.children.length === 0) {
      this.rotationOverlay.visible = false;
      return;
    }
    // Copy position & scale from TC's rotation picker child (TC sets these every frame)
    const ref = rotatePicker.children[0];
    this.rotationOverlay.position.copy(ref.position);
    this.rotationOverlay.scale.copy(ref.scale);
    this.rotationOverlay.visible = true;
  }

  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate);
    this.controls.update();

    // LOD: fade mm sub-grid when zoomed out
    const camDist = this.camera.position.distanceTo((this.controls as any).target as THREE.Vector3);
    this.grid.updateLOD(camDist);

    this.syncRotationOverlay();
    this.dimensionOverlay.updateProjections();
    this.measureTool.update();
    this.composer.render();

    // Render combined HUD (view cube + axis arrows) in top-right corner
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    const hudSize = Math.min(180, Math.floor(Math.min(w, h) * 0.22));
    if (hudSize > 20 && this.hudVisible) {
      const pad = 4;
      const hudX = w - hudSize - pad;
      const hudY = h - hudSize - pad;

      const dir = new THREE.Vector3();
      this.camera.getWorldDirection(dir);
      this.hudCamera.position.copy(dir).negate().multiplyScalar(3.0);
      this.hudCamera.up.copy(this.camera.up);
      this.hudCamera.lookAt(0, 0, 0);

      this.renderer.autoClear = false;
      this.renderer.setViewport(hudX, hudY, hudSize, hudSize);
      this.renderer.setScissor(hudX, hudY, hudSize, hudSize);
      this.renderer.setScissorTest(true);
      this.renderer.clearDepth();
      this.renderer.render(this.hudScene, this.hudCamera);

      this.renderer.setScissorTest(false);
      this.renderer.setViewport(0, 0, w, h);
      this.renderer.autoClear = true;
    }
  };

  showParts(
    parts: Array<{
      geometry: THREE.BufferGeometry;
      from: SourcePosition;
      to: SourcePosition;
      children?: Array<{ geometry: THREE.BufferGeometry; from: SourcePosition; to: SourcePosition; isAttach?: boolean; isParentBody?: boolean; isCut?: boolean; color?: [number, number, number]; opacity?: number; instance?: string; nested?: boolean }>;
      operation?: 'union' | 'difference' | 'intersection';
      color?: [number, number, number];
      opacity?: number;
      /** Colored pieces to draw instead of the combined geometry (SCAD coords). */
      display?: Array<{ geometry: THREE.BufferGeometry; color?: [number, number, number]; opacity?: number }>;
    }>,
    debugGeometry?: THREE.BufferGeometry,
  ): void {
    this.removeParts();

    const allMeshes: THREE.Object3D[] = [];
    const unionBox = new THREE.Box3();

    for (const part of parts) {
      // Manifold uses Z-up, Three.js uses Y-up: rotate -90deg around X
      part.geometry.rotateX(-Math.PI / 2);

      // Compute world-space bounding box BEFORE centering
      part.geometry.computeBoundingBox();
      if (part.geometry.boundingBox) {
        unionBox.union(part.geometry.boundingBox);
      }

      // Center geometry so gizmo appears at object center
      const center = new THREE.Vector3();
      if (part.geometry.boundingBox) {
        part.geometry.boundingBox.getCenter(center);
        part.geometry.translate(-center.x, -center.y, -center.z);
      }

      // Create material - custom colored if part has color, otherwise use default
      let meshMaterial: THREE.Material;
      if (this.isWireframe) {
        meshMaterial = this.wireframeMaterial;
      } else if (part.color || part.opacity !== undefined) {
        const color = part.color ? new THREE.Color(part.color[0], part.color[1], part.color[2]) : new THREE.Color(0xffffff);
        const opacity = part.opacity ?? 1;
        meshMaterial = new THREE.MeshPhysicalMaterial({
          color,
          metalness: 0.1,
          roughness: 0.5,
          transparent: opacity < 1,
          opacity,
          side: opacity < 1 ? THREE.DoubleSide : THREE.FrontSide,
          depthWrite: opacity >= 1,
        });
      } else {
        meshMaterial = this.material;
      }

      const mesh = new THREE.Mesh(part.geometry, meshMaterial);
      mesh.position.copy(center);
      this.scene.add(mesh);
      allMeshes.push(mesh);

      // Edge outline (from centered geometry, positioned at center)
      const edgesGeo = new THREE.EdgesGeometry(part.geometry, 15);
      const edges = new THREE.LineSegments(edgesGeo, new THREE.LineBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0.7,
      }));
      edges.position.copy(center);
      this.scene.add(edges);

      // Build invisible ghost meshes for children (raycasting for parameter editing)
      let ghostParts: GhostPart[] | undefined;
      if (part.children && part.children.length > 0) {
        ghostParts = [];

        // Separate boolean children (need offset) from attach children (positioned directly)
        const boolChildren = part.children.filter(c => !c.isAttach);
        const attachChildren = part.children.filter(c => c.isAttach);

        // Single decision: render children as individual visible meshes?
        // YES when any non-body child has a color AND the operation allows splitting
        // (union/group). For difference/intersection the combined mesh IS the correct
        // geometry and must stay visible. Same for cut-mode attach children — the
        // combined mesh is the only correct representation of subtractive operations.
        const hasCutChildren = attachChildren.some(c => c.isCut);
        const renderIndividually = !part.display?.length &&
          part.operation !== 'difference' && part.operation !== 'intersection' &&
          !hasCutChildren && (
            // nested (selection-only) ghosts never decide or take part in drawing
            boolChildren.some(c => c.color && !c.nested) ||
            attachChildren.some(c => !c.isParentBody && c.color)
          );

        // Helper: create a colored material for a child, falling back to part color
        const makeChildMaterial = (
          childColor?: [number, number, number],
          childOpacity?: number,
        ): THREE.Material => {
          if (this.isWireframe) return this.wireframeMaterial;
          const effectiveColor = childColor ?? part.color;
          const effectiveOpacity = childOpacity ?? part.opacity;
          if (effectiveColor || effectiveOpacity !== undefined) {
            const color = effectiveColor ? new THREE.Color(effectiveColor[0], effectiveColor[1], effectiveColor[2]) : new THREE.Color(0xffffff);
            const opacity = effectiveOpacity ?? 1;
            return new THREE.MeshPhysicalMaterial({
              color,
              metalness: 0.1,
              roughness: 0.5,
              transparent: opacity < 1,
              opacity,
              side: opacity < 1 ? THREE.DoubleSide : THREE.FrontSide,
              depthWrite: opacity >= 1,
            });
          }
          return this.material;
        };

        // Boolean children: compute offset from their union center to parent center
        // (accounts for outer transforms wrapping the boolean group)
        if (boolChildren.length > 0) {
          const boolUnionBox = new THREE.Box3();
          const processed: Array<{
            geometry: THREE.BufferGeometry;
            childCenter: THREE.Vector3;
            from: SourcePosition; to: SourcePosition;
            color?: [number, number, number]; opacity?: number; instance?: string; nested?: boolean;
          }> = [];

          for (const child of boolChildren) {
            child.geometry.rotateX(-Math.PI / 2);
            child.geometry.computeBoundingBox();
            const childCenter = new THREE.Vector3();
            if (child.geometry.boundingBox) {
              boolUnionBox.union(child.geometry.boundingBox);
              child.geometry.boundingBox.getCenter(childCenter);
              child.geometry.translate(-childCenter.x, -childCenter.y, -childCenter.z);
            }
            processed.push({ geometry: child.geometry, childCenter, from: child.from, to: child.to, color: child.color, opacity: child.opacity, instance: child.instance, nested: child.nested });
          }

          // Child manifolds are already produced at their final world
          // position by the evaluator (outer translate/rotate/scale baked
          // in). Place each ghost directly at its own bbox center — no
          // re-centering against the parent or against the union of sibling
          // children. The previous `boolOffset` correction pushed ghosts
          // off-axis when a child's bbox extended outside the parent result
          // (e.g. a subtrahend that didn't overlap the minuend).

          for (const pc of processed) {
            const ghostPos = pc.childCenter.clone();
            const drawn = renderIndividually && !pc.nested;
            const childMaterial = drawn
              ? makeChildMaterial(pc.color, pc.opacity)
              : this.material;
            const ghostMesh = new THREE.Mesh(pc.geometry, childMaterial);
            ghostMesh.position.copy(ghostPos);
            ghostMesh.visible = drawn;
            if (drawn) allMeshes.push(ghostMesh);
            this.scene.add(ghostMesh);
            ghostParts.push({ mesh: ghostMesh, from: pc.from, to: pc.to, center: ghostPos.clone(), instance: pc.instance });
          }
        }

        // Attach children: already positioned in parent's local space.
        for (const child of attachChildren) {
          child.geometry.rotateX(-Math.PI / 2);
          child.geometry.computeBoundingBox();
          const childCenter = new THREE.Vector3();
          if (child.geometry.boundingBox) {
            child.geometry.boundingBox.getCenter(childCenter);
            child.geometry.translate(-childCenter.x, -childCenter.y, -childCenter.z);
          }

          // Parent body is redundant when boolChildren already cover the same geometry
          const isRedundantBody = child.isParentBody && boolChildren.length > 0;

          // When rendering individually (no cuts): show all non-redundant children.
          // When cuts exist: combined mesh stays visible for correct CSG, but
          // colored add-mode children render as overlays on top (with polygonOffset).
          const visible = renderIndividually
            ? !isRedundantBody
            : (!child.isParentBody && !child.isCut && !!child.color && !this.isWireframe);

          let childMaterial: THREE.Material = this.material;
          if (visible && !this.isWireframe) {
            childMaterial = makeChildMaterial(child.color, child.opacity);
            // When overlaying on the combined mesh (cuts present), use polygonOffset
            // to ensure the colored overlay wins the depth test without z-fighting.
            if (hasCutChildren && childMaterial !== this.material) {
              (childMaterial as THREE.MeshPhysicalMaterial).polygonOffset = true;
              (childMaterial as THREE.MeshPhysicalMaterial).polygonOffsetFactor = -1;
              (childMaterial as THREE.MeshPhysicalMaterial).polygonOffsetUnits = -1;
            }
          }

          const ghostMesh = new THREE.Mesh(child.geometry, childMaterial);
          ghostMesh.position.copy(childCenter);
          ghostMesh.visible = visible;
          if (visible) allMeshes.push(ghostMesh);
          this.scene.add(ghostMesh);
          ghostParts.push({ mesh: ghostMesh, from: child.from, to: child.to, center: childCenter.clone(), instance: child.instance });
        }

        // Hide combined mesh when children are rendered individually (no cuts)
        if (renderIndividually) {
          mesh.visible = false;
        }
      }

      // Colored pieces found through modules / unions / { } blocks: draw them
      // in their own colors instead of the single-colored combined mesh.
      let displayMeshes: THREE.Mesh[] | undefined;
      if (part.display?.length) {
        displayMeshes = [];
        for (const d of part.display) {
          d.geometry.rotateX(-Math.PI / 2);
          let mat: THREE.Material = this.material;
          if (d.color || d.opacity !== undefined) {
            const opacity = d.opacity ?? 1;
            mat = new THREE.MeshPhysicalMaterial({
              color: d.color ? new THREE.Color(d.color[0], d.color[1], d.color[2]) : new THREE.Color(0xffffff),
              metalness: 0.1, roughness: 0.5,
              transparent: opacity < 1, opacity,
              side: opacity < 1 ? THREE.DoubleSide : THREE.FrontSide,
              depthWrite: opacity >= 1,
            });
          }
          const dm = new THREE.Mesh(d.geometry, mat);
          dm.visible = !this.isWireframe;
          this.scene.add(dm);
          if (dm.visible) allMeshes.push(dm);
          displayMeshes.push(dm);
        }
        mesh.visible = this.isWireframe;
      }

      this.meshParts.push({
        mesh, edges, from: part.from, to: part.to,
        initialPosition: center.clone(), children: ghostParts,
        operation: part.operation,
        color: part.color,
        opacity: part.opacity,
        displayMeshes,
      });
    }

    if (debugGeometry) {
      debugGeometry.rotateX(-Math.PI / 2);
      this.debugMesh = new THREE.Mesh(debugGeometry, this.debugMaterial);
      this.debugMesh.renderOrder = 1;
      this.scene.add(this.debugMesh);
      allMeshes.push(this.debugMesh);
    }

    // OutlinePass: silhouette on all meshes
    this.outlinePass.selectedObjects = allMeshes;

    // Grid extent from union bounding box
    if (!unionBox.isEmpty()) {
      const maxExtent = Math.max(
        Math.abs(unionBox.min.x), Math.abs(unionBox.max.x),
        Math.abs(unionBox.min.z), Math.abs(unionBox.max.z),
      );
      this.updateGridExtent(maxExtent);
    }

    // Camera fitting from union bounding box
    if (!this.cameraFitted && !unionBox.isEmpty()) {
      const sphere = new THREE.Sphere();
      unionBox.getBoundingSphere(sphere);
      this.fitCameraToSphere(sphere, true);
      this.cameraFitted = true;
    } else if (!unionBox.isEmpty()) {
      // Update near/far only
      const center = new THREE.Vector3();
      unionBox.getCenter(center);
      const size = new THREE.Vector3();
      unionBox.getSize(size);
      const radius = size.length() / 2;
      const dist = this.camera.position.distanceTo(center);
      this.updateNearFar(dist, radius);
      this.camera.updateProjectionMatrix();
    }
    this.applySectionClipping();
  }

  /**
   * Clip all model geometry with `plane` (Three world space; the half where
   * plane.distanceToPoint < 0 is removed), or remove clipping with null.
   * The silhouette OutlinePass ignores clipping, so it is paused meanwhile.
   */
  setSectionPlane(plane: THREE.Plane | null): void {
    this.sectionPlane = plane;
    this.renderer.localClippingEnabled = !!plane;
    this.outlinePass.enabled = !plane;
    this.applySectionClipping();
  }

  /** Solid cap for the cut, as SCAD-space triangles (xyz × 3 per tri), or null. */
  setSectionCaps(scadTriangles: Float32Array | null): void {
    if (this.sectionCapMesh) {
      this.scene.remove(this.sectionCapMesh);
      this.sectionCapMesh.geometry.dispose();
      (this.sectionCapMesh.material as THREE.Material).dispose();
      this.sectionCapMesh = null;
    }
    if (!scadTriangles || scadTriangles.length === 0) return;
    const pos = new Float32Array(scadTriangles.length);
    for (let i = 0; i < scadTriangles.length; i += 3) {
      // SCAD (x, y, z) → Three (x, z, -y)
      pos[i] = scadTriangles[i];
      pos[i + 1] = scadTriangles[i + 2];
      pos[i + 2] = -scadTriangles[i + 1];
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.sectionCapMesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
      color: 0xe8743b, side: THREE.DoubleSide,
      polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
    }));
    this.sectionCapMesh.renderOrder = 2;
    this.scene.add(this.sectionCapMesh);
  }

  private applySectionClipping(): void {
    const planes = this.sectionPlane ? [this.sectionPlane] : [];
    const objs: THREE.Object3D[] = [];
    for (const p of this.meshParts) {
      objs.push(p.mesh, p.edges);
      if (p.children) for (const g of p.children) objs.push(g.mesh);
      if (p.displayMeshes) objs.push(...p.displayMeshes);
    }
    if (this.currentMesh) objs.push(this.currentMesh);
    if (this.edgeLines) objs.push(this.edgeLines);
    if (this.debugMesh) objs.push(this.debugMesh);
    for (const o of objs) {
      const mat = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      for (const m of Array.isArray(mat) ? mat : mat ? [mat] : []) m.clippingPlanes = planes;
    }
  }

  showGeometry(geometry: THREE.BufferGeometry, debugGeometry?: THREE.BufferGeometry): void {
    this.removeMesh();

    // Manifold uses Z-up, Three.js uses Y-up: rotate -90deg around X
    geometry.rotateX(-Math.PI / 2);

    this.currentMesh = new THREE.Mesh(
      geometry,
      this.isWireframe ? this.wireframeMaterial : this.material,
    );
    this.scene.add(this.currentMesh);

    // Edge outline
    const edges = new THREE.EdgesGeometry(geometry, 15);
    this.edgeLines = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.7,
    }));
    this.scene.add(this.edgeLines);

    if (debugGeometry) {
      debugGeometry.rotateX(-Math.PI / 2);
      this.debugMesh = new THREE.Mesh(debugGeometry, this.debugMaterial);
      this.debugMesh.renderOrder = 1;
      this.scene.add(this.debugMesh);
    }

    // OutlinePass: silhouette outline on all meshes
    const selected: THREE.Object3D[] = [this.currentMesh];
    if (this.debugMesh) selected.push(this.debugMesh);
    this.outlinePass.selectedObjects = selected;

    // Expand grid if model extends beyond current extent
    geometry.computeBoundingBox();
    if (geometry.boundingBox) {
      const box = geometry.boundingBox;
      const maxExtent = Math.max(
        Math.abs(box.min.x), Math.abs(box.max.x),
        Math.abs(box.min.z), Math.abs(box.max.z),
      );
      this.updateGridExtent(maxExtent); // 20% padding applied in updateGridExtent
    }

    if (!this.cameraFitted) {
      this.fitToObject();
      this.cameraFitted = true;
    } else {
      // Preserve user's camera position — only update near/far for new geometry size
      geometry.computeBoundingSphere();
      const sphere = geometry.boundingSphere;
      if (sphere && sphere.radius > 0) {
        const dist = this.camera.position.distanceTo(sphere.center);
        this.updateNearFar(dist);
        this.camera.updateProjectionMatrix();
      }
    }
    this.applySectionClipping();
  }

  private createGrid(): LabeledGrid {
    // Default grid in mm; snap to 10mm, minimum 50mm half-extent
    this.gridExtent = Math.max(50, Math.ceil(this.gridExtent / 10) * 10);
    const size = this.gridExtent * 2;
    return new LabeledGrid(
      size,               // width (total, not half)
      size,               // length
      10,                 // step: major lines every 10 mm
      [0, 1, 0],          // upVector: Y-up -> grid on XZ plane
      this.themeGridColor,
      1.0,                // opacity (main=1.0, sub=0.5, margin=1.0)
      true,               // text labels
      this.themeGridTextColor,
      'center',
      this.themeGridCmBorderColor,
      this.themeGridLabelStrokeColor,
    );
  }

  private updateGridExtent(needed: number): void {
    // Extend grid to 20% beyond the max bounding box, snap to 10mm
    const newExtent = Math.max(this.gridExtent, Math.ceil(needed * 1.2 / 10) * 10);
    if (newExtent <= this.gridExtent) return;

    this.gridExtent = newExtent;

    // Rebuild grid
    this.scene.remove(this.grid);
    this.grid.dispose();
    this.grid = this.createGrid();
    this.scene.add(this.grid);
  }

  private fitCameraToSphere(sphere: THREE.Sphere, useDefaultDir = false): void {
    const center = sphere.center;
    const radius = sphere.radius;
    if (radius === 0) return;

    let distance: number;
    if (this.camera instanceof THREE.PerspectiveCamera) {
      const fov = this.camera.fov * (Math.PI / 180);
      distance = (radius / Math.sin(fov / 2)) * 1.2;
    } else {
      const ortho = this.camera as THREE.OrthographicCamera;
      const aspect = this.container.clientWidth / (this.container.clientHeight || 1);
      const halfH = radius * 1.2;
      ortho.top = halfH;
      ortho.bottom = -halfH;
      ortho.left = -halfH * aspect;
      ortho.right = halfH * aspect;
      distance = radius * 2.5;
    }

    const target = (this.controls as any).target as THREE.Vector3;
    const direction = useDefaultDir
      ? new THREE.Vector3(1, 0.8, 1).normalize()
      : new THREE.Vector3().subVectors(this.camera.position, target).normalize();

    target.copy(center);
    this.camera.position.copy(center).addScaledVector(direction, distance);
    this.updateNearFar(distance);
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  /** Compute union bounding box of all current scene meshes. */
  private getSceneBBox(): THREE.Box3 {
    const box = new THREE.Box3();
    for (const part of this.meshParts) {
      const geo = part.mesh.geometry;
      geo.computeBoundingBox();
      if (geo.boundingBox) {
        const partBox = geo.boundingBox.clone();
        partBox.translate(part.mesh.position);
        box.union(partBox);
      }
    }
    if (this.currentMesh) {
      const geo = this.currentMesh.geometry;
      geo.computeBoundingBox();
      if (geo.boundingBox) {
        box.union(geo.boundingBox);
      }
    }
    return box;
  }

  /** Fit camera to current scene objects with default isometric view angle. */
  fitToObject(): void {
    const unionBox = this.getSceneBBox();
    if (unionBox.isEmpty()) return;

    const sphere = new THREE.Sphere();
    unionBox.getBoundingSphere(sphere);
    this.fitCameraToSphere(sphere, true);

    // Update grid to match scene
    const maxExtent = Math.max(
      Math.abs(unionBox.min.x), Math.abs(unionBox.max.x),
      Math.abs(unionBox.min.z), Math.abs(unionBox.max.z),
    );
    this.updateGridExtent(maxExtent);
  }

  /** Fit camera to selection (or full scene) keeping the current view direction. */
  fitToSelection(): void {
    const sel = this.getSelectedBBox();
    const bbox = sel ? sel.bbox : this.getSceneBBox();
    if (bbox.isEmpty()) return;

    const sphere = new THREE.Sphere();
    bbox.getBoundingSphere(sphere);
    this.fitCameraToSphere(sphere, false);
  }

  /** Fit camera to face the selected object along its surface normal.
   *  Raycasts from the current camera toward the object to find the
   *  facing surface, then repositions the camera along that face's outward normal. */
  fitToSelectionNormal(): void {
    const mesh = this.selectedGhost
      ? (this.ghostHighlight ?? this.selectedGhost.mesh)
      : this.selectedPart?.mesh ?? null;
    if (!mesh) {
      this.fitToSelection();
      return;
    }

    const geo = mesh.geometry;
    geo.computeBoundingBox();
    geo.computeBoundingSphere();
    if (!geo.boundingBox || !geo.boundingSphere) return;

    // Object center in world space
    const center = new THREE.Vector3();
    geo.boundingBox.getCenter(center);
    center.add(mesh.position);

    const radius = geo.boundingSphere.radius;
    if (radius === 0) return;

    // Raycast from camera toward center to find the facing surface normal
    const direction = new THREE.Vector3().subVectors(center, this.camera.position).normalize();
    this.raycaster.set(this.camera.position, direction);
    const intersects = this.raycaster.intersectObject(mesh, false);

    let faceNormal: THREE.Vector3;
    if (intersects.length > 0 && intersects[0].face) {
      faceNormal = intersects[0].face.normal.clone();
      faceNormal.transformDirection(mesh.matrixWorld);
    } else {
      // Fallback: use camera-to-center direction (reversed)
      faceNormal = direction.clone().negate();
    }

    // Compute fit distance
    let distance: number;
    if (this.camera instanceof THREE.PerspectiveCamera) {
      const fov = this.camera.fov * (Math.PI / 180);
      distance = (radius / Math.sin(fov / 2)) * 1.2;
    } else {
      const ortho = this.camera as THREE.OrthographicCamera;
      const aspect = this.container.clientWidth / (this.container.clientHeight || 1);
      const halfH = radius * 1.2;
      ortho.top = halfH;
      ortho.bottom = -halfH;
      ortho.left = -halfH * aspect;
      ortho.right = halfH * aspect;
      distance = radius * 2.5;
    }

    // Up vector — avoid degenerate case when normal is parallel to Y
    const up = new THREE.Vector3(0, 1, 0);
    if (Math.abs(faceNormal.dot(up)) > 0.99) {
      up.set(0, 0, -1);
    }

    // Position camera along face normal
    const target = (this.controls as any).target as THREE.Vector3;
    target.copy(center);
    this.camera.position.copy(center).addScaledVector(faceNormal, distance);
    this.camera.up.copy(up);
    this.camera.lookAt(target);
    this.updateNearFar(distance);
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  showFrameBoxes(boxes: Array<{ min: [number, number, number]; max: [number, number, number] }>): void {
    this.removeFrameBoxes();
    for (const box of boxes) {
      // Convert Z-up (Manifold) to Y-up (Three.js): [x, y, z] → [x, z, -y]
      const center = new THREE.Vector3(
        (box.min[0] + box.max[0]) / 2,
        (box.min[2] + box.max[2]) / 2,
        -(box.min[1] + box.max[1]) / 2,
      );
      const size = new THREE.Vector3(
        box.max[0] - box.min[0],
        box.max[2] - box.min[2],
        box.max[1] - box.min[1],
      );
      const boxGeo = new THREE.BoxGeometry(size.x, size.y, size.z);
      const edgesGeo = new THREE.EdgesGeometry(boxGeo);
      boxGeo.dispose();
      const lines = new THREE.LineSegments(edgesGeo, this.frameMaterial);
      lines.computeLineDistances();
      lines.position.copy(center);
      this.scene.add(lines);
      this.frameLines.push(lines);
    }
  }

  private removeFrameBoxes(): void {
    for (const lines of this.frameLines) {
      this.scene.remove(lines);
      lines.geometry.dispose();
    }
    this.frameLines = [];
  }

  removeParts(): void {
    this.removeFrameBoxes();
    // Silently clear selection state (no callbacks) — the meshes are being disposed
    this.clearGhostSelection();
    this.selectedPart = null;
    this.selectionOutlinePass.selectedObjects = [];
    this.transformControls.detach();
    this.dimensionOverlay.clear();
    for (const part of this.meshParts) {
      this.scene.remove(part.mesh);
      part.mesh.geometry.dispose();
      this.scene.remove(part.edges);
      part.edges.geometry.dispose();
      (part.edges.material as THREE.Material).dispose();
      if (part.children) {
        for (const ghost of part.children) {
          this.scene.remove(ghost.mesh);
          ghost.mesh.geometry.dispose();
        }
      }
      for (const dm of part.displayMeshes ?? []) {
        this.scene.remove(dm);
        dm.geometry.dispose();
        if (dm.material !== this.material) (dm.material as THREE.Material).dispose();
      }
    }
    this.meshParts = [];
    if (this.debugMesh) {
      this.scene.remove(this.debugMesh);
      this.debugMesh.geometry.dispose();
      this.debugMesh = null;
    }
    this.outlinePass.selectedObjects = [];
  }

  removeMesh(): void {
    this.removeFrameBoxes();
    // Also remove parts if any
    if (this.meshParts.length > 0) {
      this.removeParts();
      return;
    }
    if (this.currentMesh) {
      this.scene.remove(this.currentMesh);
      this.currentMesh.geometry.dispose();
      this.currentMesh = null;
    }
    if (this.edgeLines) {
      this.scene.remove(this.edgeLines);
      this.edgeLines.geometry.dispose();
      (this.edgeLines.material as THREE.Material).dispose();
      this.edgeLines = null;
    }
    if (this.debugMesh) {
      this.scene.remove(this.debugMesh);
      this.debugMesh.geometry.dispose();
      this.debugMesh = null;
    }
    this.outlinePass.selectedObjects = [];
  }

  toggleWireframe(): boolean {
    this.isWireframe = !this.isWireframe;
    if (this.currentMesh) {
      this.currentMesh.material = this.isWireframe ? this.wireframeMaterial : this.material;
    }
    for (const part of this.meshParts) {
      part.mesh.material = this.isWireframe ? this.wireframeMaterial : this.material;
      // Colored pieces are solid-only: wireframe shows the combined mesh
      if (part.displayMeshes) {
        for (const dm of part.displayMeshes) dm.visible = !this.isWireframe;
        part.mesh.visible = this.isWireframe;
      }
    }
    return this.isWireframe;
  }

  refitNextGeometry(): void {
    this.cameraFitted = false;
  }

  setCameraTopDown(height = 15): void {
    this.controls.reset();
    const target = (this.controls as any).target as THREE.Vector3;
    target.set(0, 0, 0);
    this.camera.position.set(0, height, 0);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  resetCamera(): void {
    // Reset controls fully (clears internal rotation/zoom state)
    this.controls.reset();

    // Reset grid back to 100mm default (half-extent = 50)
    this.scene.remove(this.grid);
    this.grid.dispose();
    this.gridExtent = 50;
    this.grid = this.createGrid();
    this.scene.add(this.grid);

    // Fit to current scene objects, or fall back to default position
    const unionBox = this.getSceneBBox();
    if (!unionBox.isEmpty()) {
      const sphere = new THREE.Sphere();
      unionBox.getBoundingSphere(sphere);
      this.fitCameraToSphere(sphere, true);

      const maxExtent = Math.max(
        Math.abs(unionBox.min.x), Math.abs(unionBox.max.x),
        Math.abs(unionBox.min.z), Math.abs(unionBox.max.z),
      );
      this.updateGridExtent(maxExtent);
    } else {
      const target = (this.controls as any).target as THREE.Vector3;
      target.set(0, 0, 0);
      this.camera.position.set(15, 12, 15);
      this.camera.lookAt(0, 0, 0);
      this.camera.near = 0.01;
      this.camera.far = 2000;
      this.camera.updateProjectionMatrix();
      this.controls.update();
    }
    this.cameraFitted = true;
  }

  setWireframe(on: boolean): void {
    this.isWireframe = on;
    if (this.currentMesh) {
      this.currentMesh.material = this.isWireframe ? this.wireframeMaterial : this.material;
    }
    for (const part of this.meshParts) {
      part.mesh.material = this.isWireframe ? this.wireframeMaterial : this.material;
    }
  }

  /** Export all visible meshes as binary STL (Z-up convention). */
  exportSTL(): DataView | null {
    const group = new THREE.Group();

    for (const part of this.meshParts) {
      group.add(part.mesh.clone());
    }
    if (this.currentMesh) {
      group.add(this.currentMesh.clone());
    }
    if (group.children.length === 0) return null;

    // Undo the -90° X rotation applied during import (convert Y-up back to Z-up for STL)
    group.rotation.x = Math.PI / 2;
    group.updateMatrixWorld(true);

    const exporter = new STLExporter();
    return exporter.parse(group, { binary: true }) as DataView;
  }

  /** Export all visible meshes as 3MF (ZIP with XML model, Z-up convention). */
  export3MF(): Uint8Array | null {
    // Collect geometries and apply Z-up transform
    const rotMatrix = new THREE.Matrix4().makeRotationX(Math.PI / 2);
    const positions: number[][] = [];
    const indices: number[][] = [];
    let vertexOffset = 0;

    const addMesh = (mesh: THREE.Mesh) => {
      const geo = mesh.geometry;
      const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;
      if (!posAttr) return;

      // Build world matrix including Z-up rotation
      const worldMatrix = rotMatrix.clone().multiply(mesh.matrixWorld);

      const verts: number[] = [];
      const tris: number[] = [];
      const v = new THREE.Vector3();

      if (geo.index) {
        // Indexed geometry
        const idx = geo.index;
        for (let i = 0; i < posAttr.count; i++) {
          v.fromBufferAttribute(posAttr, i).applyMatrix4(worldMatrix);
          verts.push(v.x, v.y, v.z);
        }
        for (let i = 0; i < idx.count; i += 3) {
          tris.push(
            idx.getX(i) + vertexOffset,
            idx.getX(i + 1) + vertexOffset,
            idx.getX(i + 2) + vertexOffset,
          );
        }
        vertexOffset += posAttr.count;
      } else {
        // Non-indexed: every 3 vertices is a triangle
        for (let i = 0; i < posAttr.count; i++) {
          v.fromBufferAttribute(posAttr, i).applyMatrix4(worldMatrix);
          verts.push(v.x, v.y, v.z);
        }
        for (let i = 0; i < posAttr.count; i += 3) {
          tris.push(i + vertexOffset, i + 1 + vertexOffset, i + 2 + vertexOffset);
        }
        vertexOffset += posAttr.count;
      }

      positions.push(verts);
      indices.push(tris);
    };

    for (const part of this.meshParts) addMesh(part.mesh);
    if (this.currentMesh) addMesh(this.currentMesh);
    if (positions.length === 0) return null;

    // Build XML
    let verticesXml = '';
    for (const verts of positions) {
      for (let i = 0; i < verts.length; i += 3) {
        verticesXml += `        <vertex x="${verts[i]}" y="${verts[i + 1]}" z="${verts[i + 2]}" />\n`;
      }
    }
    let trianglesXml = '';
    for (const tris of indices) {
      for (let i = 0; i < tris.length; i += 3) {
        trianglesXml += `        <triangle v1="${tris[i]}" v2="${tris[i + 1]}" v3="${tris[i + 2]}" />\n`;
      }
    }

    const modelXml = `<?xml version="1.0" encoding="UTF-8"?>
<model unit="millimeter" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02">
  <resources>
    <object id="1" type="model">
      <mesh>
        <vertices>
${verticesXml}        </vertices>
        <triangles>
${trianglesXml}        </triangles>
      </mesh>
    </object>
  </resources>
  <build>
    <item objectid="1" />
  </build>
</model>`;

    const contentTypes = `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml" />
  <Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml" />
</Types>`;

    const rels = `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Target="/3D/3dmodel.model" Id="rel0" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel" />
</Relationships>`;

    return zipSync({
      '[Content_Types].xml': strToU8(contentTypes),
      '_rels': { '.rels': strToU8(rels) },
      '3D': { '3dmodel.model': strToU8(modelXml) },
    });
  }

  // --- Projection toggle ---

  private rebuildComposer(cam: THREE.PerspectiveCamera | THREE.OrthographicCamera): void {
    const savedOutline = this.outlinePass.selectedObjects.slice();
    const savedSelection = this.selectionOutlinePass.selectedObjects.slice();
    const w = this.container.clientWidth || 1;
    const h = this.container.clientHeight || 1;

    this.composer.dispose();
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, cam));

    this.outlinePass = new OutlinePass(new THREE.Vector2(w, h), this.scene, cam);
    this.outlinePass.edgeStrength = 3;
    this.outlinePass.edgeGlow = 0;
    this.outlinePass.edgeThickness = 1;
    this.outlinePass.visibleEdgeColor.set(this.themeOutlineVisibleColor);
    this.outlinePass.hiddenEdgeColor.set(this.themeOutlineHiddenColor);
    this.outlinePass.selectedObjects = savedOutline;
    this.composer.addPass(this.outlinePass);

    this.selectionOutlinePass = new OutlinePass(new THREE.Vector2(w, h), this.scene, cam);
    this.selectionOutlinePass.edgeStrength = 5;
    this.selectionOutlinePass.edgeGlow = 0.5;
    this.selectionOutlinePass.edgeThickness = 2;
    this.selectionOutlinePass.visibleEdgeColor.set(0xffaa00);
    this.selectionOutlinePass.hiddenEdgeColor.set(0xffaa00);
    this.selectionOutlinePass.selectedObjects = savedSelection;
    this.composer.addPass(this.selectionOutlinePass);

    this.composer.addPass(new OutputPass());
    this.composer.setSize(w, h);
  }

  toggleProjection(): boolean {
    this.isOrthographic = !this.isOrthographic;

    const oldCam = this.camera;
    const oldTarget = ((this.controls as any).target as THREE.Vector3).clone();
    const dist = oldCam.position.distanceTo(oldTarget);
    const aspect = this.container.clientWidth / (this.container.clientHeight || 1);

    if (this.isOrthographic) {
      // --- Switching to ortho: pause ArcballControls, create OrbitControls ---
      const fov = this.perspCamera.fov * (Math.PI / 180);
      const halfH = Math.tan(fov / 2) * dist;
      // Use wide near/far for ortho — depth is linear so precision is fine.
      // Negative near lets objects behind the camera render (avoids front clipping).
      const orthoFar = Math.max(2000, dist * 4);
      const orthoCam = new THREE.OrthographicCamera(
        -halfH * aspect, halfH * aspect, halfH, -halfH,
        -orthoFar, orthoFar,
      );
      orthoCam.position.copy(oldCam.position);
      orthoCam.quaternion.copy(oldCam.quaternion);
      orthoCam.up.copy(oldCam.up);
      orthoCam.updateProjectionMatrix();

      // Pause ArcballControls (don't dispose — we'll reuse it)
      this.arcControls.enabled = false;
      (this.arcControls as any).disconnect();

      // Create OrbitControls for ortho
      const orbit = new OrbitControls(orthoCam, this.renderer.domElement);
      orbit.target.copy(oldTarget);
      orbit.enableDamping = false;
      // Cursor-centered zoom — wheel zooms into whatever the cursor points at
      // (matches ArcballControls' cursorZoom behavior in perspective mode).
      orbit.zoomToCursor = true;
      // Pan in screen space so Shift+drag / middle-drag moves the scene
      // under the cursor, not along world axes.
      orbit.screenSpacePanning = true;
      // Slightly faster zoom feel in ortho (wheel delta feels small otherwise).
      orbit.zoomSpeed = 1.4;
      orbit.panSpeed = 1.2;
      orbit.update();
      this.controls = orbit;
      this.camera = orthoCam;
    } else {
      // --- Switching back to persp: restore ArcballControls ---
      const cam = this.perspCamera;
      const arc = this.arcControls;

      // Dispose ortho OrbitControls
      this.controls.dispose();

      // Restore camera pose — use world-up (0,1,0) as the base up vector,
      // matching ArcballControls' expectation that _upState = (0,1,0).
      cam.position.copy(oldCam.position);
      cam.up.set(0, 1, 0);
      // Don't copy ortho near/far (negative near breaks perspective).
      // Compute appropriate values from distance to target.
      cam.near = Math.max(0.01, dist * 0.01);
      cam.far = Math.max(2000, dist * 10);
      cam.lookAt(oldTarget.x, oldTarget.y, oldTarget.z);
      cam.updateMatrix();
      cam.updateProjectionMatrix();

      // Manually set all ArcballControls internal state (bypass setCamera to
      // avoid its lookAt side-effects and ensure _upState stays at (0,1,0)).
      (arc as any).target.copy(oldTarget);
      (arc as any)._currentTarget.copy(oldTarget);

      // Camera matrix states
      (arc as any)._cameraMatrixState0.copy(cam.matrix);
      (arc as any)._cameraMatrixState.copy(cam.matrix);
      (arc as any)._cameraProjectionState.copy(cam.projectionMatrix);

      // Up state — MUST be (0,1,0) for correct rotation behavior
      (arc as any)._up0.set(0, 1, 0);
      (arc as any)._upState.set(0, 1, 0);

      // FOV/zoom state
      (arc as any)._fov0 = cam.fov;
      (arc as any)._fovState = cam.fov;
      (arc as any)._zoom0 = cam.zoom;
      (arc as any)._zoomState = cam.zoom;

      // Near/far state
      (arc as any)._initialNear = cam.near;
      (arc as any)._nearPos0 = cam.position.distanceTo(oldTarget) - cam.near;
      (arc as any)._nearPos = cam.near;
      (arc as any)._initialFar = cam.far;
      (arc as any)._farPos0 = cam.position.distanceTo(oldTarget) - cam.far;
      (arc as any)._farPos = cam.far;

      // Gizmo state — position at target
      (arc as any)._gizmoMatrixState0.identity().setPosition(oldTarget);
      (arc as any)._gizmoMatrixState.copy((arc as any)._gizmoMatrixState0);
      (arc as any)._gizmos.position.copy(oldTarget);
      (arc as any)._gizmos.quaternion.identity();
      (arc as any)._gizmos.scale.set(1, 1, 1);

      // Recalculate trackball radius
      (arc as any)._tbRadius = (arc as any).calculateTbRadius(cam);

      // Re-enable and reconnect
      arc.enabled = true;
      (arc as any).connect();
      arc.setGizmosVisible(false);

      this.controls = arc;
      this.camera = cam;
    }

    // Update all references
    (this.transformControls as any).camera = this.camera;
    this.rebuildComposer(this.camera);
    this.dimensionOverlay.setCamera(this.camera);

    return this.isOrthographic;
  }

  // --- Standard views ---

  /** Classify where on a cube face the hit landed: center, edge, or corner zone. */
  private classifyHitZone(
    point: THREE.Vector3, halfSize: number, materialIndex: number,
  ): { type: 'face'; faceIndex: number } |
     { type: 'edge'; faceIndex: number; adjacentIndex: number } |
     { type: 'corner'; faceIndex: number; adj1Index: number; adj2Index: number } {
    const t = 0.35; // threshold as fraction of half-size (lower = bigger edge/corner zones)
    // materialIndex: 0,1 → X axis; 2,3 → Y axis; 4,5 → Z axis
    const faceAxisMap = [0, 0, 1, 1, 2, 2];
    const faceAxis = faceAxisMap[materialIndex];
    const axes = [0, 1, 2].filter(a => a !== faceAxis);
    const comps = [point.x, point.y, point.z];

    const adjacentFaces: number[] = [];
    for (const axis of axes) {
      const n = comps[axis] / halfSize;
      if (Math.abs(n) > t) {
        // axis 0(X): +→0(Right) -→1(Left); axis 1(Y): +→2(Top) -→3(Bottom); axis 2(Z): +→4(Front) -→5(Back)
        adjacentFaces.push(axis * 2 + (n < 0 ? 1 : 0));
      }
    }

    if (adjacentFaces.length === 0) {
      return { type: 'face', faceIndex: materialIndex };
    } else if (adjacentFaces.length === 1) {
      return { type: 'edge', faceIndex: materialIndex, adjacentIndex: adjacentFaces[0] };
    } else {
      return { type: 'corner', faceIndex: materialIndex, adj1Index: adjacentFaces[0], adj2Index: adjacentFaces[1] };
    }
  }

  /** Navigate camera to look from a given direction. Auto-computes up vector if not provided. */
  private navigateToView(dir: THREE.Vector3, up?: THREE.Vector3): void {
    if (!up) {
      up = new THREE.Vector3(0, 1, 0);
      if (Math.abs(dir.dot(up)) > 0.99) {
        up = new THREE.Vector3(0, 0, -1);
      }
    }

    const sel = this.getSelectedBBox();
    const bbox = sel ? sel.bbox : this.getSceneBBox();
    const center = new THREE.Vector3();
    let radius = 5;
    if (!bbox.isEmpty()) {
      const sphere = new THREE.Sphere();
      bbox.getBoundingSphere(sphere);
      center.copy(sphere.center);
      radius = sphere.radius;
    }

    let distance: number;
    if (this.camera instanceof THREE.PerspectiveCamera) {
      const fov = this.camera.fov * (Math.PI / 180);
      distance = (radius / Math.sin(fov / 2)) * 1.2;
    } else {
      const ortho = this.camera as THREE.OrthographicCamera;
      const aspect = this.container.clientWidth / (this.container.clientHeight || 1);
      const halfH = radius * 1.2;
      ortho.top = halfH;
      ortho.bottom = -halfH;
      ortho.left = -halfH * aspect;
      ortho.right = halfH * aspect;
      distance = radius * 2.5;
    }

    this.controls.reset();
    const target = (this.controls as any).target as THREE.Vector3;
    target.copy(center);
    this.camera.position.copy(center).addScaledVector(dir, distance);
    this.camera.up.copy(up);
    this.camera.lookAt(target);
    this.updateNearFar(distance);
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  // Camera state saved when entering the sketch "3D BG" mode so we can put
  // the camera back exactly where the user left it when they exit.
  private sketchViewSaved: ViewSnapshot | null = null;

  // Zoom the current ortho frustum to a requested world width/height so the
  // sketch editor's 2D scale stays visually matched with the 3D ortho.
  setSketchOrthoSize(worldWidth: number, worldHeight: number): void {
    if (!(this.camera instanceof THREE.OrthographicCamera)) return;
    this.camera.left = -worldWidth / 2;
    this.camera.right = worldWidth / 2;
    this.camera.top = worldHeight / 2;
    this.camera.bottom = -worldHeight / 2;
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  // Cursor-centered zoom: resize the ortho frustum by `factor` AND shift the
  // camera target so that the world point under the screen cursor stays
  // under the cursor after the zoom. This mirrors what the 2D editor does
  // on scroll so the two views stay locked together.
  zoomSketchView(cursorClientX: number, cursorClientY: number, factor: number): void {
    if (!(this.camera instanceof THREE.OrthographicCamera)) return;
    const cam = this.camera;
    const rect = this.container.getBoundingClientRect();
    const cursorOffsetXpx = (cursorClientX - rect.left) - rect.width / 2;
    const cursorOffsetYpx = (cursorClientY - rect.top) - rect.height / 2;

    const wBefore = cam.right - cam.left;
    const hBefore = cam.top - cam.bottom;
    const cursorBeforeX = cursorOffsetXpx * wBefore / rect.width;
    const cursorBeforeY = -cursorOffsetYpx * hBefore / rect.height; // screen Y down → world Y up

    cam.left /= factor;
    cam.right /= factor;
    cam.top /= factor;
    cam.bottom /= factor;

    const wAfter = cam.right - cam.left;
    const hAfter = cam.top - cam.bottom;
    const cursorAfterX = cursorOffsetXpx * wAfter / rect.width;
    const cursorAfterY = -cursorOffsetYpx * hAfter / rect.height;

    // Target must shift by (before - after) so cursor stays put.
    const dRight = cursorBeforeX - cursorAfterX;
    const dUp = cursorBeforeY - cursorAfterY;
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(cam.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(cam.quaternion);
    const offset = new THREE.Vector3()
      .addScaledVector(right, dRight)
      .addScaledVector(up, dUp);
    cam.position.add(offset);
    const target = (this.controls as { target: THREE.Vector3 }).target;
    target.add(offset);

    cam.updateProjectionMatrix();
    this.controls.update();
  }

  // Generic view capture — returns a snapshot the caller can hold onto and
  // later apply with applyViewSnapshot(). Used by the sketch editor to
  // remember both the user's original view and their last BG-mode view.
  captureViewSnapshot(): ViewSnapshot {
    const target = ((this.controls as { target: THREE.Vector3 }).target).clone();
    const s: ViewSnapshot = {
      isOrthographic: this.isOrthographic,
      position: this.camera.position.clone(),
      quaternion: this.camera.quaternion.clone(),
      up: this.camera.up.clone(),
      target,
    };
    if (this.camera instanceof THREE.OrthographicCamera) {
      s.ortho = { left: this.camera.left, right: this.camera.right, top: this.camera.top, bottom: this.camera.bottom };
    }
    return s;
  }

  applyViewSnapshot(s: ViewSnapshot): void {
    if (s.isOrthographic !== this.isOrthographic) this.toggleProjection();
    this.camera.position.copy(s.position);
    this.camera.quaternion.copy(s.quaternion);
    this.camera.up.copy(s.up);
    const target = (this.controls as { target: THREE.Vector3 }).target;
    target.copy(s.target);
    if (this.camera instanceof THREE.OrthographicCamera && s.ortho) {
      this.camera.left = s.ortho.left;
      this.camera.right = s.ortho.right;
      this.camera.top = s.ortho.top;
      this.camera.bottom = s.ortho.bottom;
      this.camera.updateProjectionMatrix();
    }
    this.controls.update();
  }

  saveSketchViewState(): void { this.sketchViewSaved = this.captureViewSnapshot(); }

  restoreSketchViewState(): void {
    if (this.sketchViewSaved) { this.applyViewSnapshot(this.sketchViewSaved); this.sketchViewSaved = null; }
  }

  // Orient the 3D camera head-on to a sketch plane. The camera is placed
  // along the plane normal, targeting the sketch origin (not the scene
  // bbox center) so the sketch coordinate system sits at screen center.
  viewSketchPlane(
    plane: string | [number, number, number],
    origin: [number, number, number],
    upScad?: [number, number, number],
  ): void {
    if (!this.isOrthographic) this.toggleProjection();

    // SCAD → Three conversion for normals / directions: (x, y, z) → (x, z, -y).
    // Named-plane normals are picked to match the rotationForPlane extrusion
    // direction (XY→+Z, XZ→-Y, YZ→+X), so the camera always sits on the
    // SAME side of the plane as the extrusion grows. This makes the sketch
    // plane face the user and the extruded solid goes AWAY into the screen,
    // matching standard CAD "sketch on plane" behaviour.
    const nScad = typeof plane === 'string'
      ? (plane.toUpperCase() === 'XY' ? [0, 0, 1]
       : plane.toUpperCase() === 'XZ' ? [0, -1, 0]
       : plane.toUpperCase() === 'YZ' ? [1, 0, 0]
       : [0, 0, 1])
      : plane;
    const dirThree = new THREE.Vector3(nScad[0], nScad[2], -nScad[1]).normalize();

    let up: THREE.Vector3;
    if (upScad) {
      up = new THREE.Vector3(upScad[0], upScad[2], -upScad[1]).normalize();
    } else {
      const upFirst = new THREE.Vector3(0, 1, 0);
      up = Math.abs(dirThree.dot(upFirst)) > 0.95 ? new THREE.Vector3(1, 0, 0) : upFirst;
    }

    // Sketch origin in Three coords.
    const [ox, oy, oz] = origin;
    const targetThree = new THREE.Vector3(ox, oz, -oy);

    // Distance + ortho frustum sized to the scene bbox so the visible region
    // comfortably contains the model.
    const bbox = this.getSceneBBox();
    let radius = 5;
    if (!bbox.isEmpty()) {
      const sphere = new THREE.Sphere();
      bbox.getBoundingSphere(sphere);
      radius = Math.max(5, sphere.radius);
    }
    let distance: number;
    if (this.camera instanceof THREE.PerspectiveCamera) {
      const fov = this.camera.fov * Math.PI / 180;
      distance = (radius / Math.sin(fov / 2)) * 1.2;
    } else {
      const ortho = this.camera;
      const aspect = this.container.clientWidth / (this.container.clientHeight || 1);
      const halfH = radius * 1.2;
      ortho.top = halfH;
      ortho.bottom = -halfH;
      ortho.left = -halfH * aspect;
      ortho.right = halfH * aspect;
      distance = radius * 2.5;
    }

    this.controls.reset();
    const controlTarget = (this.controls as { target: THREE.Vector3 }).target;
    controlTarget.copy(targetThree);
    this.camera.position.copy(targetThree).addScaledVector(dirThree, distance);
    this.camera.up.copy(up);
    this.camera.lookAt(controlTarget);
    this.updateNearFar(distance);
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  // Pan the 3D camera in the current view plane by the given world-space
  // delta (mm). Called by the 2D sketch editor when its canvas pans, so the
  // model behind the sketch stays aligned.
  panSketchView(worldDX: number, worldDY: number): void {
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);
    const offset = new THREE.Vector3()
      .addScaledVector(right, -worldDX)
      .addScaledVector(up, -worldDY);
    this.camera.position.add(offset);
    const target = (this.controls as { target: THREE.Vector3 }).target;
    target.add(offset);
    this.controls.update();
  }

  // Show / hide the LabeledGrid (used to declutter the 3D view when it sits
  // behind a 2D sketch).
  setGridVisible(visible: boolean): void {
    (this.grid as unknown as { visible: boolean }).visible = visible;
  }

  // Face-pick preview: while enabled, the triangle under the cursor is
  // highlighted in amber so the user can see which face they're about to
  // pick for "sketch on face".
  private faceHoverMode = false;
  private faceHighlightMesh: THREE.Mesh | null = null;
  setFaceHoverMode(enabled: boolean): void {
    this.faceHoverMode = enabled;
    if (!enabled && this.faceHighlightMesh) this.faceHighlightMesh.visible = false;
    this.renderer.domElement.style.cursor = enabled ? 'crosshair' : '';
  }
  private updateFaceHighlight(e: PointerEvent): void {
    if (!this.faceHoverMode) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(mouse, this.camera);
    const targets: THREE.Object3D[] = [];
    for (const part of this.meshParts) {
      if (part.children && part.children.length > 0) {
        for (const ghost of part.children) targets.push(ghost.mesh);
        targets.push(part.mesh);
      } else {
        targets.push(part.mesh);
      }
    }
    if (targets.length === 0) {
      if (this.faceHighlightMesh) this.faceHighlightMesh.visible = false;
      return;
    }
    const intersects = this.raycaster.intersectObjects(targets, false);
    const hit = intersects.find(i => i.face);
    if (!hit || !hit.face) {
      if (this.faceHighlightMesh) this.faceHighlightMesh.visible = false;
      return;
    }
    const mesh = hit.object as THREE.Mesh;
    const geom = mesh.geometry;
    const pos = geom.getAttribute('position') as THREE.BufferAttribute;

    // Reference plane: normal + signed distance from origin.
    const a0 = new THREE.Vector3().fromBufferAttribute(pos, hit.face.a);
    const b0 = new THREE.Vector3().fromBufferAttribute(pos, hit.face.b);
    const c0 = new THREE.Vector3().fromBufferAttribute(pos, hit.face.c);
    const hitNormal = new THREE.Vector3().subVectors(b0, a0).cross(new THREE.Vector3().subVectors(c0, a0)).normalize();
    const hitD = hitNormal.dot(a0);

    // Collect every triangle on the same plane (same normal direction and
    // matching plane-offset). This catches a cube's 2 face triangles, a
    // cylinder's end cap fan, etc., in a single hover highlight.
    const coplanarVerts: number[] = [];
    const index = geom.index;
    const triCount = index ? index.count / 3 : pos.count / 3;
    const va = new THREE.Vector3();
    const vb = new THREE.Vector3();
    const vc = new THREE.Vector3();
    const edge1 = new THREE.Vector3();
    const edge2 = new THREE.Vector3();
    const tn = new THREE.Vector3();
    const EPS_N = 1e-3;
    const EPS_D = 1e-3;
    for (let t = 0; t < triCount; t++) {
      const ai = index ? index.getX(t * 3)     : t * 3;
      const bi = index ? index.getX(t * 3 + 1) : t * 3 + 1;
      const ci = index ? index.getX(t * 3 + 2) : t * 3 + 2;
      va.fromBufferAttribute(pos, ai);
      vb.fromBufferAttribute(pos, bi);
      vc.fromBufferAttribute(pos, ci);
      edge1.subVectors(vb, va);
      edge2.subVectors(vc, va);
      tn.crossVectors(edge1, edge2).normalize();
      if (tn.dot(hitNormal) < 1 - EPS_N) continue; // same-direction normal only
      if (Math.abs(tn.dot(va) - hitD) > EPS_D) continue; // same plane offset
      // Same-plane triangle: transform to world and emit vertices.
      const wa = va.clone().applyMatrix4(mesh.matrixWorld);
      const wb = vb.clone().applyMatrix4(mesh.matrixWorld);
      const wc = vc.clone().applyMatrix4(mesh.matrixWorld);
      coplanarVerts.push(wa.x, wa.y, wa.z, wb.x, wb.y, wb.z, wc.x, wc.y, wc.z);
    }

    if (!this.faceHighlightMesh) {
      const g = new THREE.BufferGeometry();
      const m = new THREE.MeshBasicMaterial({
        color: 0xffb347, transparent: true, opacity: 0.55,
        depthTest: false, depthWrite: false, side: THREE.DoubleSide,
      });
      this.faceHighlightMesh = new THREE.Mesh(g, m);
      this.faceHighlightMesh.renderOrder = 999;
      this.scene.add(this.faceHighlightMesh);
    }
    const highlightGeom = this.faceHighlightMesh.geometry as THREE.BufferGeometry;
    highlightGeom.setAttribute('position', new THREE.Float32BufferAttribute(coplanarVerts, 3));
    highlightGeom.computeBoundingSphere();
    this.faceHighlightMesh.visible = coplanarVerts.length > 0;
  }

  // Walk the coplanar triangle group containing `hit` and return:
  //   - boundary edges (count==1 across the group) as SCAD-coord segments
  //   - the area-weighted centroid of the group, in SCAD coords
  // Used by "New Sketch from Face" to pre-populate the sketch with the
  // outline of the picked face and (optionally) anchor its origin at the
  // face center rather than the click point.
  private extractCoplanarBoundaryScad(
    hit: THREE.Intersection,
  ): { edges: [number, number, number][][]; center: [number, number, number] | null } {
    if (!hit.face) return { edges: [], center: null };
    const mesh = hit.object as THREE.Mesh;
    const geom = mesh.geometry as THREE.BufferGeometry;
    const pos = geom.getAttribute('position') as THREE.BufferAttribute;
    const a0 = new THREE.Vector3().fromBufferAttribute(pos, hit.face.a);
    const b0 = new THREE.Vector3().fromBufferAttribute(pos, hit.face.b);
    const c0 = new THREE.Vector3().fromBufferAttribute(pos, hit.face.c);
    const hitNormal = new THREE.Vector3().subVectors(b0, a0).cross(new THREE.Vector3().subVectors(c0, a0)).normalize();
    const hitD = hitNormal.dot(a0);

    const index = geom.index;
    const triCount = index ? index.count / 3 : pos.count / 3;
    const va = new THREE.Vector3();
    const vb = new THREE.Vector3();
    const vc = new THREE.Vector3();
    const edge1 = new THREE.Vector3();
    const edge2 = new THREE.Vector3();
    const tn = new THREE.Vector3();
    const EPS_N = 1e-3;
    const EPS_D = 1e-3;

    // Quantize world-space coords to 1 µm grid so vertices that are
    // duplicated across triangles (separate BufferGeometry indices but the
    // same physical point) collapse onto a single key, letting the
    // edge-count match across triangles.
    const Q = 1000;
    const keyOf = (v: THREE.Vector3): string => `${Math.round(v.x * Q)},${Math.round(v.y * Q)},${Math.round(v.z * Q)}`;
    const verts = new Map<string, THREE.Vector3>();
    type EdgeRec = { count: number; a: string; b: string };
    const edges = new Map<string, EdgeRec>();
    const addEdge = (ka: string, kb: string): void => {
      const k = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
      const rec = edges.get(k);
      if (rec) rec.count += 1;
      else edges.set(k, { count: 1, a: ka, b: kb });
    };

    // Area-weighted centroid accumulator (in three.js world coords).
    let cX = 0, cY = 0, cZ = 0, totArea = 0;

    for (let t = 0; t < triCount; t++) {
      const ai = index ? index.getX(t * 3)     : t * 3;
      const bi = index ? index.getX(t * 3 + 1) : t * 3 + 1;
      const ci = index ? index.getX(t * 3 + 2) : t * 3 + 2;
      va.fromBufferAttribute(pos, ai);
      vb.fromBufferAttribute(pos, bi);
      vc.fromBufferAttribute(pos, ci);
      edge1.subVectors(vb, va);
      edge2.subVectors(vc, va);
      tn.crossVectors(edge1, edge2);
      const triArea2 = tn.length(); // 2× signed-area magnitude
      if (triArea2 < 1e-9) continue;
      tn.divideScalar(triArea2); // normalize
      if (tn.dot(hitNormal) < 1 - EPS_N) continue;
      if (Math.abs(tn.dot(va) - hitD) > EPS_D) continue;
      const wa = va.clone().applyMatrix4(mesh.matrixWorld);
      const wb = vb.clone().applyMatrix4(mesh.matrixWorld);
      const wc = vc.clone().applyMatrix4(mesh.matrixWorld);
      const ka = keyOf(wa); const kb = keyOf(wb); const kc = keyOf(wc);
      if (!verts.has(ka)) verts.set(ka, wa);
      if (!verts.has(kb)) verts.set(kb, wb);
      if (!verts.has(kc)) verts.set(kc, wc);
      addEdge(ka, kb);
      addEdge(kb, kc);
      addEdge(kc, ka);

      // Triangle centroid (in world space) weighted by its area.
      const triCx = (wa.x + wb.x + wc.x) / 3;
      const triCy = (wa.y + wb.y + wc.y) / 3;
      const triCz = (wa.z + wb.z + wc.z) / 3;
      const w = triArea2 * 0.5;
      cX += triCx * w; cY += triCy * w; cZ += triCz * w;
      totArea += w;
    }

    const out: [number, number, number][][] = [];
    for (const rec of edges.values()) {
      if (rec.count !== 1) continue;
      const va2 = verts.get(rec.a); const vb2 = verts.get(rec.b);
      if (!va2 || !vb2) continue;
      // three.js (Y-up) → SCAD (Z-up): (x, y, z)_scad = (x, -z, y)_three
      out.push([
        [va2.x, -va2.z, va2.y],
        [vb2.x, -vb2.z, vb2.y],
      ]);
    }

    let center: [number, number, number] | null = null;
    if (totArea > 1e-9) {
      const wx = cX / totArea, wy = cY / totArea, wz = cZ / totArea;
      // Y-up → SCAD Z-up.
      center = [wx, -wz, wy];
    }
    return { edges: out, center };
  }

  // Toggle the non-model 3D chrome as a group — view cube HUD, selection
  // outline, dimension overlay, and transform gizmo. Used by the 2D sketch
  // "3D BG" mode so the background shows only the bare model.
  private hudVisible = true;
  setViewportChromeVisible(visible: boolean): void {
    this.hudVisible = visible;
    this.selectionOutlinePass.enabled = visible;
    this.dimensionOverlay.setVisible(visible);
    this.transformControls.getHelper().visible = visible;
    this.transformControls.enabled = visible;
  }

  // Set the opacity of every model mesh's material. 1 = fully opaque,
  // 0 = invisible. Sketch 3D-BG mode uses this to fade the model so the
  // sketch on top is easier to see.
  setModelOpacity(opacity: number): void {
    const apply = (mat: THREE.Material | THREE.Material[]): void => {
      const mats = Array.isArray(mat) ? mat : [mat];
      for (const m of mats) {
        const mm = m as THREE.Material & { opacity: number; transparent: boolean };
        mm.opacity = opacity;
        mm.transparent = opacity < 0.999;
        mm.needsUpdate = true;
      }
    };
    for (const part of this.meshParts) {
      apply(part.mesh.material);
      if (part.children) {
        for (const ghost of part.children) apply(ghost.mesh.material);
      }
    }
  }

  // Return the current ortho view's extent expressed in sketch-local
  // coordinates (the camera's right/up axes map to the sketch's X/Y). Used
  // by the 2D editor to fit its canvas pan/zoom to match the 3D view.
  getSketchViewExtent(): { cx: number; cy: number; worldWidth: number; worldHeight: number } | null {
    if (!(this.camera instanceof THREE.OrthographicCamera)) return null;
    const cam = this.camera;
    const target = (this.controls as { target: THREE.Vector3 }).target;
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(cam.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(cam.quaternion);
    return {
      cx: target.dot(right),
      cy: target.dot(up),
      worldWidth: cam.right - cam.left,
      worldHeight: cam.top - cam.bottom,
    };
  }

  setStandardView(view: 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom'): void {
    const viewDirs: Record<string, { dir: THREE.Vector3; up: THREE.Vector3 }> = {
      front:  { dir: new THREE.Vector3(0, 0, 1),  up: new THREE.Vector3(0, 1, 0) },
      back:   { dir: new THREE.Vector3(0, 0, -1), up: new THREE.Vector3(0, 1, 0) },
      left:   { dir: new THREE.Vector3(-1, 0, 0), up: new THREE.Vector3(0, 1, 0) },
      right:  { dir: new THREE.Vector3(1, 0, 0),  up: new THREE.Vector3(0, 1, 0) },
      top:    { dir: new THREE.Vector3(0, 1, 0),  up: new THREE.Vector3(0, 0, -1) },
      bottom: { dir: new THREE.Vector3(0, -1, 0), up: new THREE.Vector3(0, 0, 1) },
    };

    const { dir, up } = viewDirs[view];
    this.navigateToView(dir, up);
  }

  private makeAxisLabel(text: string, color: number, position: THREE.Vector3): THREE.Sprite {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    ctx.font = 'bold 48px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.strokeStyle = 'rgba(0,0,0,0.6)';
    ctx.lineWidth = 4;
    ctx.strokeText(text, size / 2, size / 2);
    ctx.fillStyle = `#${new THREE.Color(color).getHexString()}`;
    ctx.fillText(text, size / 2, size / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const mat = new THREE.SpriteMaterial({ map: texture, depthTest: false });
    const sprite = new THREE.Sprite(mat);
    sprite.position.copy(position);
    sprite.scale.set(0.35, 0.35, 1);
    return sprite;
  }

  // ========== User-defined origin (work coordinate system) ==========

  private buildOriginGizmo(): THREE.Group {
    const group = new THREE.Group();
    group.renderOrder = 4;
    const L = 6;        // arrow length (scene units)
    const headLen = 1.5;
    const headW = 0.9;
    const origin = new THREE.Vector3(0, 0, 0);
    group.add(new THREE.ArrowHelper(new THREE.Vector3(1, 0, 0), origin, L, 0xff4444, headLen, headW));
    group.add(new THREE.ArrowHelper(new THREE.Vector3(0, 1, 0), origin, L, 0x4488ff, headLen, headW));  // Three Y = SCAD Z (blue)
    group.add(new THREE.ArrowHelper(new THREE.Vector3(0, 0, 1), origin, L, 0x44cc44, headLen, headW));  // Three Z = SCAD Y (green)
    const sphereGeo = new THREE.SphereGeometry(0.35, 16, 12);
    const sphereMat = new THREE.MeshBasicMaterial({ color: 0xe6b54e, depthTest: false });
    const sphere = new THREE.Mesh(sphereGeo, sphereMat);
    sphere.renderOrder = 5;
    group.add(sphere);
    group.visible = false;
    return group;
  }

  private loadUserOriginFromStorage(): void {
    try {
      const raw = localStorage.getItem(ViewportManager.ORIGIN_STORAGE_KEY);
      if (!raw) return;
      const obj = JSON.parse(raw) as { x: number; y: number; z: number };
      // Stored in SCAD coords (x, y, z). Convert to Three: (sx, sz, -sy).
      const three = new THREE.Vector3(obj.x, obj.z, -obj.y);
      this.applyUserOrigin(three, /* save */ false);
    } catch { /* ignore */ }
  }

  private saveUserOriginToStorage(): void {
    try {
      // Convert Three → SCAD: (x, -z, y)
      const scad = { x: this.userOrigin.x, y: -this.userOrigin.z, z: this.userOrigin.y };
      localStorage.setItem(ViewportManager.ORIGIN_STORAGE_KEY, JSON.stringify(scad));
    } catch { /* ignore */ }
  }

  private applyUserOrigin(threeVec: THREE.Vector3, save: boolean): void {
    this.userOrigin.copy(threeVec);
    if (this.originGroup) {
      this.originGroup.position.copy(this.userOrigin);
      this.originGroup.visible = this.userOrigin.lengthSq() > 1e-6;
    }
    if (save) this.saveUserOriginToStorage();
    for (const cb of this.originChangeCallbacks) cb(this.userOrigin.clone());
  }

  /** Returns a clone of the current user origin in Three.js (Y-up) coords. */
  getUserOrigin(): THREE.Vector3 {
    return this.userOrigin.clone();
  }

  /** Returns the current user origin in SCAD (Z-up) coords as a plain tuple. */
  getUserOriginScad(): [number, number, number] {
    return [this.userOrigin.x, -this.userOrigin.z, this.userOrigin.y];
  }

  /** Set origin from SCAD-coord tuple (user-intuitive). */
  setUserOriginScad(x: number, y: number, z: number): void {
    this.applyUserOrigin(new THREE.Vector3(x, z, -y), /* save */ true);
  }

  /** Set origin from Three.js Vector3. */
  setUserOrigin(v: THREE.Vector3): void {
    this.applyUserOrigin(v.clone(), /* save */ true);
  }

  /** Drop the origin at the currently selected object's bbox center. */
  setUserOriginToSelection(): boolean {
    const sel = this.getSelectedBBox();
    if (!sel) return false;
    const c = new THREE.Vector3();
    sel.bbox.getCenter(c);
    this.applyUserOrigin(c, true);
    return true;
  }

  resetUserOrigin(): void {
    this.applyUserOrigin(new THREE.Vector3(0, 0, 0), /* save */ true);
  }

  onOriginChange(cb: (origin: THREE.Vector3) => void): void {
    this.originChangeCallbacks.push(cb);
  }

  // ==================================================================

  applyTheme(theme: ViewportTheme): void {
    // Clear color — kept transparent so the CSS blueprint grid shows through.
    // theme.clearColor is unused visually but retained for consumers that need it.
    void theme.clearColor;
    this.renderer.setClearColor(0x000000, 0);

    // Grid: rebuild with new colors
    this.themeGridColor = theme.gridColor;
    this.themeGridTextColor = theme.gridTextColor;
    this.themeGridCmBorderColor = theme.gridCmBorderColor;
    this.themeGridLabelStrokeColor = theme.gridLabelStrokeColor;
    this.scene.remove(this.grid);
    this.grid.dispose();
    this.grid = this.createGrid();
    this.scene.add(this.grid);

    // Outline passes
    this.themeOutlineVisibleColor = theme.outlineVisibleColor;
    this.themeOutlineHiddenColor = theme.outlineHiddenColor;
    this.outlinePass.visibleEdgeColor.set(theme.outlineVisibleColor);
    this.outlinePass.hiddenEdgeColor.set(theme.outlineHiddenColor);

    // View cube faces
    const materials = (this.viewCubeMesh.material as THREE.MeshBasicMaterial[]);
    const labels = ['Right', 'Left', 'Top', 'Bottom', 'Front', 'Back'];
    for (let i = 0; i < materials.length; i++) {
      const mat = materials[i];
      const oldTex = mat.map;
      const s = 256;
      const canvas = document.createElement('canvas');
      canvas.width = s;
      canvas.height = s;
      const ctx = canvas.getContext('2d')!;
      const c = new THREE.Color(theme.viewCubeFaceColors[i]);
      ctx.fillStyle = `rgb(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)})`;
      ctx.fillRect(0, 0, s, s);
      const edgeC = new THREE.Color(theme.viewCubeEdgeColor);
      ctx.strokeStyle = `rgba(${Math.round(edgeC.r * 255)},${Math.round(edgeC.g * 255)},${Math.round(edgeC.b * 255)},0.3)`;
      ctx.lineWidth = 4;
      ctx.strokeRect(2, 2, s - 4, s - 4);
      ctx.font = 'bold 52px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = theme.viewCubeTextColor;
      ctx.fillText(labels[i], s / 2, s / 2);
      const texture = new THREE.CanvasTexture(canvas);
      mat.map = texture;
      mat.needsUpdate = true;
      if (oldTex) oldTex.dispose();
    }

    // View cube edges
    const edgeMat = this.viewCubeEdges.material as THREE.LineBasicMaterial;
    edgeMat.color.setHex(theme.viewCubeEdgeColor);
    edgeMat.opacity = theme.viewCubeEdgeOpacity;

    // Dimension overlay
    this.dimensionOverlay.applyTheme(theme.dimensionLineColor, theme.dimensionArrowColor);
  }

  dispose(): void {
    cancelAnimationFrame(this.animationId);
    this.measureTool.dispose();
    this.resizeObserver.disconnect();

    this.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.renderer.domElement.removeEventListener('pointerup', this.onPointerUp);
    this.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);

    this.transformControls.detach();
    this.transformControls.dispose();

    this.removeParts();
    this.removeMesh();
    this.dimensionOverlay.dispose();
    this.material.dispose();
    this.wireframeMaterial.dispose();
    this.debugMaterial.dispose();
    this.ghostHighlightMaterial.dispose();
    this.frameMaterial.dispose();
    this.renderer.dispose();
    this.controls.dispose();
    // If arcControls is paused (ortho mode), dispose it too
    if (this.controls !== this.arcControls) {
      this.arcControls.dispose();
    }

    // Clean up HUD scene (view cube + axis arrows + labels)
    this.hudScene.traverse((obj) => {
      if (obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof THREE.Sprite) {
        obj.geometry?.dispose();
        const mat = obj.material;
        if (Array.isArray(mat)) mat.forEach(m => {
          if (m.map) m.map.dispose();
          m.dispose();
        });
        else {
          if ((mat as any).map) (mat as any).map.dispose();
          mat.dispose();
        }
      }
    });

    // Clean up grid (handles lines, labels, border internally)
    this.scene.remove(this.grid);
    this.grid.dispose();
  }
}
